import {
  styles
} from "./chunk-TSA7YIXI.js";

// src/components/charts/bars/BarChart.tsx
import { useRef } from "react";
import { Bar } from "react-chartjs-2";

// src/components/charts/chartjs.utils.tsx
import { getElementAtEvent, getElementsAtEvent, getDatasetAtEvent } from "react-chartjs-2";
function buildChartjsOnClick(chartRef, onClick) {
  return (event) => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    onClick?.({
      event,
      elementAtEvent: getElementAtEvent(chart, event),
      elementsAtEvent: getElementsAtEvent(chart, event),
      datasetAtEvent: getDatasetAtEvent(chart, event)
    });
  };
}

// src/components/charts/bars/BarChart.tsx
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LogarithmicScale
} from "chart.js";

// src/styles/styles.utils.ts
var numericRegex = /^-?\d+(\.\d+)?$/;
var getStyleValue = (variableName) => {
  if (typeof window === "undefined" || typeof document === "undefined") return "";
  const computedStyle = getComputedStyle(document.documentElement);
  return computedStyle.getPropertyValue(variableName).trim();
};
var getStyle = (variableName, fallbackValue) => {
  const rawValue = getStyleValue(variableName);
  if (!rawValue) return fallbackValue ?? rawValue;
  const firstChar = rawValue.charAt(0);
  if (firstChar === "#" || rawValue.startsWith("rgb(") || rawValue.startsWith("rgba(")) {
    return rawValue;
  }
  return rawValue;
};
var getStyleNumber = ((variableName, fallbackValue) => {
  const rawValue = getStyleValue(variableName) || fallbackValue;
  if (!rawValue) return void 0;
  const numericValue = parseFloat(rawValue);
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
  if (rawValue.slice(-2) === "px") {
    return numericValue;
  }
  const unit3 = rawValue.slice(-3);
  if (unit3 === "rem") {
    return numericValue * rootFontSize;
  }
  if (numericRegex.test(rawValue)) {
    return numericValue;
  }
  return void 0;
});

// src/components/charts/charts.constants.ts
var getChartColors = () => [
  getStyle("--em-sem-chart-color--1"),
  getStyle("--em-sem-chart-color--2"),
  getStyle("--em-sem-chart-color--3"),
  getStyle("--em-sem-chart-color--4"),
  getStyle("--em-sem-chart-color--5"),
  getStyle("--em-sem-chart-color--6"),
  getStyle("--em-sem-chart-color--7"),
  getStyle("--em-sem-chart-color--8"),
  getStyle("--em-sem-chart-color--9"),
  getStyle("--em-sem-chart-color--10")
];

// src/components/charts/bars/bars.utils.ts
import { mergician as mergician2 } from "mergician";

// src/components/charts/chartjs.cartesian.constants.ts
import { mergician } from "mergician";

// src/components/charts/chartjs.constants.ts
var getChartjsOptionsPlugins = () => ({
  datalabels: {
    backgroundColor: getStyle("--em-chart-label-background", "#FFF"),
    borderRadius: getStyleNumber("--em-chart-label-border-radius", "624.9375rem"),
    padding: {
      top: getStyleNumber("--em-chart-label-padding-top-bottom", "0.125rem"),
      bottom: getStyleNumber("--em-chart-label-padding-top-bottom", "0.125rem"),
      right: getStyleNumber("--em-chart-label-padding-left-right", "0.25rem"),
      left: getStyleNumber("--em-chart-label-padding-left-right", "0.25rem")
    },
    color: getStyle("--em-chart-label-color", "#212129"),
    font: {
      size: getStyleNumber("--em-chart-label-font-size", "0.75rem"),
      weight: getStyleNumber("--em-chart-label-font-weight", "500"),
      family: getStyle("--em-chart-label-font-family", "sans-serif")
    }
  },
  legend: {
    position: "bottom",
    labels: {
      boxWidth: getStyleNumber("--em-chart-category-size", "0.75rem") / 2,
      boxHeight: getStyleNumber("--em-chart-category-size", "0.75rem") / 2,
      usePointStyle: true,
      color: getStyle("--em-chart-category-color", "#5C5C66"),
      padding: getStyleNumber("--em-chart-category-list-gap", "1rem"),
      font: {
        family: getStyle("--em-chart-category-font-family", "sans-serif"),
        size: getStyleNumber("--em-chart-category-font-size", "0.75rem"),
        weight: getStyleNumber("--em-chart-category-font-weight", "500")
      }
    }
  },
  tooltip: {
    usePointStyle: true,
    caretSize: 0,
    enabled: true,
    backgroundColor: getStyle("--em-chart-tooltip-background", "#212129"),
    cornerRadius: getStyleNumber("--em-chart-tooltip-border-radius", "0.75rem"),
    padding: getStyleNumber("--em-chart-tooltip-padding", "1rem"),
    displayColors: true,
    bodyColor: getStyle("--em-chart-tooltip-title-color", "#FFF"),
    bodyAlign: "left",
    boxPadding: getStyleNumber("--em-chart-category-gap", "0.25rem"),
    bodyFont: {
      size: getStyleNumber("--em-chart-category-font-size", "0.75rem"),
      weight: getStyleNumber("--em-chart-category-font-weight", "500"),
      family: getStyle("--em-chart-tooltip-family", "sans-serif")
    },
    titleAlign: "left",
    titleColor: getStyle("--em-chart-tooltip-title-color", "#FFF"),
    titleFont: {
      size: getStyleNumber("--em-chart-tooltip-title-font-size", "0.875rem"),
      weight: getStyleNumber("--em-chart-tooltip-title-font-weight", "700"),
      family: getStyle("--em-chart-tooltip-family", "sans-serif")
    }
  }
});
var getChartjsOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: getChartjsOptionsPlugins()
});

// src/components/charts/chartjs.cartesian.constants.ts
var getChartjsAxisOptionsPlugins = () => ({
  datalabels: {
    labels: {
      total: {
        anchor: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value >= 0 ? "end" : "start";
        },
        align: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value >= 0 ? "top" : "bottom";
        }
      },
      value: {
        anchor: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value >= 0 ? "end" : "start";
        },
        align: (context) => {
          const value = context.dataset.data[context.dataIndex];
          return value >= 0 ? "top" : "bottom";
        }
      }
    }
  }
});
var getChartjsAxisOptionsScalesTicksDefault = () => ({
  display: true,
  color: getStyle("--em-chart-grid-label-color", "#212129"),
  font: {
    size: getStyleNumber("--em-chart-grid-label-font-size", "0.75rem"),
    family: getStyle("--em-chart-grid-font-family", "sans-serif"),
    weight: getStyleNumber("--em-chart-grid-label-font-weight", "400")
  }
});
var getChartjsAxisOptionsScalesTicksMuted = () => ({
  display: true,
  color: getStyle("--em-chart-grid-label-color--muted", "#5C5C66"),
  font: {
    size: getStyleNumber("--em-chart-grid-label-font-size", "0.75rem"),
    family: getStyle("--em-chart-grid-font-family", "sans-serif"),
    weight: getStyleNumber("--em-chart-grid-label-font-weight", "400")
  }
});
var getChartjsAxisOptionsScalesTitle = () => ({
  color: getStyle("--em-chart-grid-label-color", "#212129"),
  font: {
    size: getStyleNumber("--em-chart-grid-title-font-size", "0.875rem"),
    weight: getStyleNumber("--em-chart-grid-title-font-weight", "700"),
    family: getStyle("--em-chart-grid-font-family", "sans-serif")
  }
});
var getChartjsAxisOptionsScalesGrid = () => ({
  color: getStyle("--em-chart-grid-line-color--light", "#EDEDF1"),
  lineWidth: getStyleNumber("--em-chart-grid-line-width--thin", "0.0625rem")
});
var afterBuildTicks = (scale) => {
  const allValues = scale.chart.data.datasets.flatMap((ds) => ds.data).filter((v) => typeof v === "number");
  if (!allValues.length || !allValues.every((v) => Number.isInteger(v))) return;
  scale.ticks = scale.ticks.filter((tick) => Number.isInteger(tick.value));
};
var getChartjsAxisOptionsScales = () => ({
  x: {
    grid: getChartjsAxisOptionsScalesGrid(),
    title: getChartjsAxisOptionsScalesTitle(),
    ticks: getChartjsAxisOptionsScalesTicksDefault(),
    afterBuildTicks
  },
  y: {
    grid: getChartjsAxisOptionsScalesGrid(),
    title: getChartjsAxisOptionsScalesTitle(),
    ticks: getChartjsAxisOptionsScalesTicksMuted(),
    afterBuildTicks
  }
});
var getChartjsAxisOptions = () => mergician(getChartjsOptions(), {
  plugins: getChartjsAxisOptionsPlugins(),
  scales: getChartjsAxisOptionsScales()
});
var chartjsAxisOptionsLayoutPadding = 30;
var chartjsAxisOptionsLayoutPaddingLarge = 60;
function isMixedValues(values) {
  const hasPos = values.some((v) => v > 0);
  const hasNeg = values.some((v) => v < 0);
  return hasPos && hasNeg;
}
var getChartjsAxisOptionsScalesGridColor = (ctx) => {
  if (ctx.tick.value === 0) {
    if (ctx.chart.data.datasets.length === 0) {
      return void 0;
    }
    const values = ctx.chart.data.datasets[0].data;
    if (isMixedValues(values)) {
      return getStyle("--em-chart-grid-line-color", "#212129");
    }
    return getStyle("--em-chart-grid-line-color--subtle", "#B8B8BD");
  }
  return getStyle("--em-chart-grid-line-color--light", "#EDEDF1");
};

// src/components/charts/bars/bars.utils.ts
var getBarChartData = (data) => {
  const chartColors = getChartColors();
  return {
    ...data,
    datasets: data.datasets?.map((dataset, index) => {
      const colors = chartColors[index % chartColors.length];
      const defaultDataset = {
        ...dataset,
        backgroundColor: colors,
        borderColor: colors
      };
      return mergician2(defaultDataset, dataset);
    })
  };
};
var getDatalabelTotalDisplay = (context, showTotalLabels) => showTotalLabels && context.datasetIndex === context.chart.data.datasets.length - 1 ? "auto" : false;
var getDatalabelTotalFormatter = (_value, context) => {
  const { datasets } = context.chart.data;
  const i = context.dataIndex;
  const total = datasets.reduce((sum, ds) => {
    const val = ds.data[i];
    return sum + (val || 0);
  }, 0);
  return total === 0 ? "" : total;
};
var getBarVerticalChartOptions = (config) => {
  return {
    indexAxis: "x",
    layout: {
      padding: {
        top: (config.stacked ? config.showTotalLabels : config.showValueLabels) ? chartjsAxisOptionsLayoutPadding : 0
      }
    },
    plugins: {
      datalabels: {
        labels: {
          total: {
            align: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value >= 0 ? "top" : "bottom";
            }
          },
          value: {
            align: () => {
              if (config.stacked) {
                return "center";
              }
              return "top";
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        border: {
          display: false
        },
        ticks: getChartjsAxisOptionsScalesTicksDefault(),
        reverse: config.reverseXAxis
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: getChartjsAxisOptionsScalesGridColor
        },
        border: {
          display: false
        },
        ticks: getChartjsAxisOptionsScalesTicksMuted(),
        min: config.yAxisRangeMin,
        max: config.yAxisRangeMax,
        type: config.showLogarithmicScale ? "logarithmic" : "linear"
      }
    }
  };
};
var getBarHorizontalChartOptions = (config) => {
  return {
    indexAxis: "y",
    layout: {
      padding: {
        right: (config.stacked ? config.showTotalLabels : config.showValueLabels) ? chartjsAxisOptionsLayoutPaddingLarge : 0
      }
    },
    plugins: {
      datalabels: {
        labels: {
          total: {
            align: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value >= 0 ? "right" : "left";
            }
          },
          value: {
            align: () => {
              if (config.stacked) {
                return "center";
              }
              return "right";
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: true, color: getChartjsAxisOptionsScalesGridColor },
        border: { display: false },
        ticks: getChartjsAxisOptionsScalesTicksMuted(),
        min: config.xAxisRangeMin,
        max: config.xAxisRangeMax,
        type: config.showLogarithmicScale ? "logarithmic" : "linear"
      },
      y: {
        grid: { display: false },
        border: {
          display: false
        },
        ticks: getChartjsAxisOptionsScalesTicksDefault(),
        reverse: config.reverseYAxis
      }
    }
  };
};
var getBarChartOptions = (options) => {
  const baseBarChartOptions = {
    elements: {
      bar: {
        borderRadius: getStyleNumber("--em-barchart-border-radius", "0.375rem")
      }
    },
    plugins: {
      legend: { display: options.showLegend },
      tooltip: {
        enabled: options.showTooltips
      },
      datalabels: {
        display: (context) => {
          return options.showValueLabels && context.dataset.data[context.dataIndex] !== 0 ? "auto" : false;
        },
        labels: {
          total: {
            display: (context) => getDatalabelTotalDisplay(context, options.showTotalLabels),
            formatter: getDatalabelTotalFormatter,
            anchor: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value >= 0 ? "end" : "start";
            }
          },
          value: {
            anchor: (context) => {
              if (options.stacked) {
                return "center";
              }
              const value = context.dataset.data[context.dataIndex];
              return value >= 0 ? "end" : "start";
            }
          }
        }
      }
    },
    scales: {
      x: {
        stacked: Boolean(options.stacked),
        title: {
          display: Boolean(options.xAxisLabel),
          text: options.xAxisLabel ?? ""
        },
        ticks: { display: true }
      },
      y: {
        stacked: Boolean(options.stacked),
        title: {
          display: Boolean(options.yAxisLabel),
          text: options.yAxisLabel ?? ""
        },
        ticks: { display: true }
      }
    }
  };
  const getVerticalHorizontalOptions = options.horizontal ? getBarHorizontalChartOptions : getBarVerticalChartOptions;
  const verticalHorizontalOptions = getVerticalHorizontalOptions(options);
  return mergician2(getChartjsAxisOptions(), baseBarChartOptions, verticalHorizontalOptions);
};

// src/components/charts/bars/BarChart.tsx
import styles2 from "./charts.module-YNKCWRLU.module.css";
import { mergician as mergician3 } from "mergician";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { jsx } from "react/jsx-runtime";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LogarithmicScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);
var BarChart = ({ data, onClick, options = {}, ...props }) => {
  const chartRef = useRef(null);
  const barChartOptions = mergician3(getBarChartOptions(props), options);
  return /* @__PURE__ */ jsx("div", { className: styles2.chartContainer, children: /* @__PURE__ */ jsx(
    Bar,
    {
      ref: chartRef,
      data: getBarChartData(data),
      options: barChartOptions,
      onClick: buildChartjsOnClick(chartRef, onClick)
    }
  ) });
};

// src/components/charts/lines/LineChart.tsx
import {
  Chart as ChartJS2,
  CategoryScale as CategoryScale2,
  LinearScale as LinearScale2,
  PointElement,
  LineElement,
  Title as Title2,
  Tooltip as Tooltip2,
  Legend as Legend2,
  LogarithmicScale as LogarithmicScale2,
  Filler
} from "chart.js";
import { Line } from "react-chartjs-2";
import styles3 from "./charts.module-YNKCWRLU.module.css";
import { useRef as useRef2 } from "react";
import { mergician as mergician5 } from "mergician";

// src/components/charts/lines/lines.utils.ts
import { mergician as mergician4 } from "mergician";
var getLineChartData = (data) => {
  const chartColors = getChartColors();
  const mergedData = {
    ...data,
    datasets: data.datasets?.map((dataset, index) => {
      const colors = chartColors[index % chartColors.length];
      const defaultDataset = {
        backgroundColor: colors,
        borderColor: colors
      };
      const merged = mergician4(defaultDataset, dataset);
      return merged;
    }) || []
  };
  return mergedData;
};
var getLineChartOptions = (options) => {
  const newOptions = {
    interaction: {
      mode: "x",
      intersect: false
    },
    elements: {
      point: {
        radius: getStyleNumber("--em-linechart-point-size", "0.5rem") / 2,
        hoverRadius: getStyleNumber("--em-linechart-point-size--hover", "0.75rem") / 2
      },
      line: {
        tension: 0.2,
        borderWidth: getStyleNumber("--em-linechart-line-width", "0.125rem")
      }
    },
    layout: {
      padding: {
        top: options.showValueLabels ? chartjsAxisOptionsLayoutPadding : 0
      }
    },
    plugins: {
      datalabels: {
        display: (context) => {
          if (!options.showValueLabels) return false;
          const value = context.dataset.data[context.dataIndex];
          const yScale = context.chart.scales.y;
          return value !== void 0 && yScale && value >= yScale.min && value <= yScale.max ? "auto" : false;
        }
      },
      legend: {
        display: options.showLegend
      },
      tooltip: {
        enabled: options.showTooltips
      }
    },
    scales: {
      x: {
        grid: { display: false },
        border: {
          display: false
        },
        ticks: getChartjsAxisOptionsScalesTicksDefault(),
        title: {
          display: Boolean(options.xAxisLabel),
          text: options.xAxisLabel
        },
        reverse: options.reverseXAxis
      },
      y: {
        beginAtZero: true,
        ticks: getChartjsAxisOptionsScalesTicksMuted(),
        grid: { display: true, color: getChartjsAxisOptionsScalesGridColor },
        border: {
          display: false
        },
        type: options.showLogarithmicScale ? "logarithmic" : "linear",
        title: {
          display: Boolean(options.yAxisLabel),
          text: options.yAxisLabel
        },
        min: options.yAxisRangeMin,
        max: options.yAxisRangeMax
      }
    }
  };
  return mergician4(getChartjsAxisOptions(), newOptions);
};

// src/components/charts/lines/LineChart.tsx
import ChartDataLabels2 from "chartjs-plugin-datalabels";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { jsx as jsx2 } from "react/jsx-runtime";
ChartJS2.register(
  CategoryScale2,
  LinearScale2,
  PointElement,
  LineElement,
  LogarithmicScale2,
  Filler,
  Title2,
  Tooltip2,
  Legend2,
  ChartDataLabels2,
  AnnotationPlugin
);
var LineChart = ({ options = {}, data, onClick, ...props }) => {
  const chartRef = useRef2(null);
  const lineOptions = mergician5(getLineChartOptions(props), options);
  return /* @__PURE__ */ jsx2("div", { className: styles3.chartContainer, children: /* @__PURE__ */ jsx2(
    Line,
    {
      ref: chartRef,
      data: getLineChartData(data),
      options: lineOptions,
      onClick: buildChartjsOnClick(chartRef, onClick)
    }
  ) });
};

// src/components/charts/scatter/ScatterChart/ScatterChart.tsx
import {
  Chart as ChartJS3,
  LinearScale as LinearScale3,
  LogarithmicScale as LogarithmicScale3,
  PointElement as PointElement2,
  LineElement as LineElement2,
  ScatterController,
  Title as Title3,
  Tooltip as Tooltip3,
  Legend as Legend3
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import styles4 from "./charts.module-YNKCWRLU.module.css";
import { useRef as useRef3 } from "react";
import { mergician as mergician7 } from "mergician";
import ChartDataLabels3 from "chartjs-plugin-datalabels";
import AnnotationPlugin2 from "chartjs-plugin-annotation";

// src/components/charts/scatter/scatter.utils.ts
import { mergician as mergician6 } from "mergician";

// src/components/charts/scatter/scatter.nullBand.utils.ts
var NULL_BAND_OFFSET = 0.25;
var NULL_BAND_PADDING = 0.08;
var createEmptyExtent = () => ({
  min: Infinity,
  max: -Infinity,
  count: 0
});
var isMissingAxisValue = (value) => value === null || value === void 0 || !Number.isFinite(value);
var accumulateExtent = (ext, value) => {
  ext.min = Math.min(ext.min, value);
  ext.max = Math.max(ext.max, value);
  ext.count += 1;
};
var getExtentBounds = (ext) => {
  if (ext.count === 0) return { min: 0, max: 0 };
  return { min: ext.min, max: ext.max };
};
var getDatasetsNullsAndExtents = (datasets) => {
  let foundNullX = false;
  let foundNullY = false;
  const xExt = createEmptyExtent();
  const yExt = createEmptyExtent();
  for (const ds of datasets) {
    for (const pt of ds.data) {
      if (isMissingAxisValue(pt.x)) foundNullX = true;
      else accumulateExtent(xExt, pt.x);
      if (isMissingAxisValue(pt.y)) foundNullY = true;
      else accumulateExtent(yExt, pt.y);
    }
  }
  return { foundNullX, foundNullY, xExt, yExt };
};
var computeAxisMin = (hasNullOnAxis, nullPos, range) => {
  if (!hasNullOnAxis) return void 0;
  return nullPos - NULL_BAND_PADDING * range;
};
var computeScatterNullBand = (datasets) => {
  if (!datasets.length) return null;
  const { foundNullX, foundNullY, xExt, yExt } = getDatasetsNullsAndExtents(datasets);
  if (!foundNullX && !foundNullY) return null;
  const { min: xNumMin, max: xNumMax } = getExtentBounds(xExt);
  const { min: yNumMin, max: yNumMax } = getExtentBounds(yExt);
  const xRange = Math.max(xNumMax - xNumMin, 1);
  const yRange = Math.max(yNumMax - yNumMin, 1);
  const xNullPos = xNumMin - NULL_BAND_OFFSET * xRange;
  const yNullPos = yNumMin - NULL_BAND_OFFSET * yRange;
  return {
    xNullPos,
    yNullPos,
    hasNullX: foundNullX,
    hasNullY: foundNullY,
    computedXAxisMin: computeAxisMin(foundNullX, xNullPos, xRange),
    computedYAxisMin: computeAxisMin(foundNullY, yNullPos, yRange),
    xRange,
    yRange,
    xNumMin,
    xNumMax,
    yNumMin,
    yNumMax
  };
};
var createScatterNullBandPlugin = (nullBand) => {
  const { xNullPos, yNullPos, hasNullX, hasNullY, xNumMin, yNumMin } = nullBand;
  return {
    id: "scatterNullBand",
    afterBuildTicks(_chart, args) {
      const scale = args.scale;
      if (!scale) return;
      if (scale.id === "x" && hasNullX) {
        const ticks = scale.ticks.filter((t) => t.value >= xNumMin);
        scale.ticks = [{ value: xNullPos }, ...ticks];
      }
      if (scale.id === "y" && hasNullY) {
        const ticks = scale.ticks.filter((t) => t.value >= yNumMin);
        scale.ticks = [{ value: yNullPos }, ...ticks];
      }
    }
  };
};

// src/utils/number.utils.ts
var numberFormat = new Intl.NumberFormat();

// src/components/charts/scatter/scatter.utils.ts
var isMeasureMissing = (v) => v === null || v === void 0 || typeof v === "number" && !Number.isFinite(v);
var hasNullMeasure = (pt) => {
  if (!pt || typeof pt !== "object") return false;
  if (pt.isNull) return true;
  return isMeasureMissing(pt.x) || isMeasureMissing(pt.y);
};
var filterNumericScatterData = (data) => ({
  ...data,
  datasets: data.datasets.map((ds) => ({
    ...ds,
    data: ds.data.filter(
      (p) => p.x !== null && p.x !== void 0 && p.y !== null && p.y !== void 0 && Number.isFinite(p.x) && Number.isFinite(p.y) && p.y > 0
    )
  }))
});
var getPointCaption = (raw) => {
  if (!raw || typeof raw !== "object") return void 0;
  return raw.pointLabel ?? raw.label;
};
var getOriginalScatterPoint = (context) => {
  const ds = context.dataset;
  return ds.originalData?.[context.dataIndex] ?? ds.data[context.dataIndex];
};
var getColorWithOpacity = (color, alpha) => {
  if (!color?.trim()) {
    return `rgba(33, 33, 41, ${alpha})`;
  }
  if (typeof document === "undefined") {
    return color;
  }
  const el = document.createElement("span");
  el.style.color = color;
  document.body.appendChild(el);
  const rgb = getComputedStyle(el).color;
  el.remove();
  const m = rgb.match(/rgba?\(\s*(\d+)\s*(?:,\s*|\s+)(\d+)\s*(?:,\s*|\s+)(\d+)/i);
  if (!m) {
    return `rgba(33, 33, 41, ${alpha})`;
  }
  return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
};
var getSeriesColor = (chartColors, index) => chartColors[index % chartColors.length] ?? "";
var isUserControlledPointFill = (dataset) => dataset.pointBackgroundColor !== void 0 || dataset.backgroundColor !== void 0;
var computeAxisMin2 = (userMin, computed) => {
  if (computed === void 0) return userMin;
  if (userMin === void 0) return computed;
  return Math.min(userMin, computed);
};
var formatMeasureValue = (_axis, value, nullLabel) => {
  if (isMeasureMissing(value)) return nullLabel ?? "No value";
  return numberFormat.format(value);
};
var formatAxisTickValue = (tickValue) => {
  const v = typeof tickValue === "number" ? tickValue : Number(tickValue);
  if (!Number.isFinite(v)) return String(tickValue);
  return numberFormat.format(v);
};
var getScatterTooltipLabel = (ctx, nullLabel, formatMeasureValue2) => {
  const ds = ctx.dataset;
  const orig = ds.originalData?.[ctx.dataIndex];
  const prefix = ds.label ? `${ds.label}: ` : "";
  if (orig) {
    return `${prefix}(${formatMeasureValue2("x", orig.x, nullLabel)}, ${formatMeasureValue2("y", orig.y, nullLabel)})`;
  }
  const { x, y } = ctx.parsed;
  const fx = typeof x === "number" ? formatMeasureValue2("x", x, nullLabel) : String(x);
  const fy = typeof y === "number" ? formatMeasureValue2("y", y, nullLabel) : String(y);
  return `${prefix}(${fx}, ${fy})`;
};
var getScatterDataWithNullBand = (data, ctx) => {
  const chartColors = getChartColors();
  const defaultOpacity = ctx.opacityOverride?.defaultOpacity ?? getStyleNumber("--em-scatterchart-point-opacity", "0.8");
  const nullOpacity = ctx.opacityOverride?.nullOpacity ?? getStyleNumber("--em-scatterchart-point-opacity--null", "0.3");
  const pointRadiusPx = getStyleNumber("--em-scatterchart-point-radius", "0.375rem");
  const pointHoverRadiusPx = getStyleNumber("--em-scatterchart-point-radius--hover", "0.5rem");
  const useNullBandMapping = ctx.supportsNullMeasures && ctx.nullBand !== null;
  return {
    ...data,
    datasets: data.datasets?.map((dataset, index) => {
      const baseColor = getSeriesColor(chartColors, index);
      const defaultDataset = {
        showLine: false,
        clip: false,
        pointRadius: pointRadiusPx,
        pointHoverRadius: pointHoverRadiusPx
      };
      if (!isUserControlledPointFill(dataset)) {
        const defaultColor = getColorWithOpacity(baseColor, defaultOpacity);
        const nullColor = getColorWithOpacity(baseColor, nullOpacity);
        const colorFor = (c) => {
          const orig = c.dataset.originalData?.[c.dataIndex] ?? c.dataset.data[c.dataIndex];
          return hasNullMeasure(orig) ? nullColor : defaultColor;
        };
        defaultDataset.pointBackgroundColor = colorFor;
        defaultDataset.pointBorderColor = colorFor;
        defaultDataset.backgroundColor = baseColor;
        defaultDataset.borderColor = baseColor;
      } else {
        defaultDataset.backgroundColor = dataset.backgroundColor ?? baseColor;
        defaultDataset.borderColor = dataset.borderColor ?? baseColor;
      }
      if (!useNullBandMapping || !ctx.nullBand) {
        return mergician6(
          defaultDataset,
          { originalData: [...dataset.data] },
          dataset
        );
      }
      const { xNullPos, yNullPos } = ctx.nullBand;
      const mappedData = dataset.data.map((pt) => ({
        x: isMeasureMissing(pt.x) ? xNullPos : pt.x,
        y: isMeasureMissing(pt.y) ? yNullPos : pt.y
      }));
      return mergician6(defaultDataset, dataset, {
        data: mappedData,
        originalData: [...dataset.data]
      });
    }) || []
  };
};
var getScatterNullBand = (datasets, showLogarithmicScale) => {
  if (showLogarithmicScale) return null;
  return computeScatterNullBand(datasets);
};
var getScatterChartPlugins = (nullBand) => {
  const plugin = nullBand ? createScatterNullBandPlugin(nullBand) : void 0;
  return plugin ? [plugin] : void 0;
};
var isValueLabelVisible = (context, showValueLabels) => {
  if (!showValueLabels) return false;
  const raw = getOriginalScatterPoint(context);
  if (!raw || typeof raw !== "object") return false;
  const yScale = context.chart.scales.y;
  const xScale = context.chart.scales.x;
  if (!yScale || !xScale) return false;
  const mapped = context.dataset.data[context.dataIndex];
  if (!mapped || typeof mapped.x !== "number" || typeof mapped.y !== "number") return false;
  const { x: mx, y: my } = mapped;
  if (yScale.type === "logarithmic" && (my <= 0 || !Number.isFinite(my))) return false;
  if (xScale.type === "logarithmic" && (mx <= 0 || !Number.isFinite(mx))) return false;
  return my >= yScale.min && my <= yScale.max && mx >= xScale.min && mx <= xScale.max;
};
var isPointLabelVisible = (context, showPointLabels) => {
  if (!showPointLabels) return false;
  const raw = getOriginalScatterPoint(context);
  return Boolean(getPointCaption(raw));
};
var getScatterChartAxisBorderPatch = () => {
  const color = getStyle("--em-chart-grid-line-color--subtle", "#B8B8BD");
  const width = Math.max(1, getStyleNumber("--em-chart-grid-line-width--thin", "1px"));
  const border = { display: true, color, width };
  return {
    scales: {
      x: { border },
      y: { border }
    }
  };
};
var getScatterBaseOptions = (options, nullContext) => {
  const pointRadius = getStyleNumber("--em-scatterchart-point-radius", "0.375rem");
  const pointHoverRadius = getStyleNumber("--em-scatterchart-point-radius--hover", "0.5rem");
  const borderWidth = getStyleNumber("--em-scatterchart-point-border-width", "0.0625rem");
  const labelStackHeight = getStyleNumber("--em-scatterchart-label-stack-height", "1.25rem");
  const labelStackGap = getStyleNumber("--em-scatterchart-label-stack-gap", "0.25rem");
  const labelOffset = getStyleNumber("--em-scatterchart-label-offset", "0.375rem");
  const labelTopPadding = getStyleNumber("--em-scatterchart-label-top-padding", "1.75rem");
  const labelStrokeWidth = getStyleNumber("--em-scatterchart-label-stroke-width", "3");
  const labelLift = pointRadius + labelOffset;
  const nullBand = nullContext?.nullBand;
  const nullBandLabel = nullContext?.nullBandLabel;
  const applyNullX = !options.showLogarithmicScale && Boolean(nullBand?.hasNullX);
  const applyNullY = !options.showLogarithmicScale && Boolean(nullBand?.hasNullY);
  const ticksDefault = getChartjsAxisOptionsScalesTicksDefault();
  const ticksMuted = getChartjsAxisOptionsScalesTicksMuted();
  const valueLabelOffset = (context) => {
    const showValueLabel = isValueLabelVisible(context, options.showValueLabels);
    const showPointLabel = isPointLabelVisible(context, options.showPointLabels);
    if (!showValueLabel) return 0;
    if (showPointLabel) return labelLift + labelStackHeight;
    return labelLift;
  };
  const captionLabelOffset = (context) => {
    const showValueLabel = isValueLabelVisible(context, options.showValueLabels);
    const showPointLabel = isPointLabelVisible(context, options.showPointLabels);
    if (!showPointLabel) return 0;
    if (showValueLabel) return labelLift + labelStackGap;
    return labelLift;
  };
  const newOptions = {
    interaction: {
      mode: "nearest",
      intersect: true
    },
    elements: {
      point: {
        radius: pointRadius,
        hoverRadius: pointHoverRadius,
        borderWidth
      }
    },
    layout: {
      padding: {
        top: options.showValueLabels || options.showPointLabels ? chartjsAxisOptionsLayoutPadding + pointRadius + labelTopPadding : 0
      }
    },
    plugins: {
      datalabels: {
        backgroundColor: "transparent",
        borderWidth: 0,
        padding: 0,
        color: getStyle("--em-chart-label-color", "#212129"),
        textStrokeColor: getStyle("--em-chart-label-background", "#FFF"),
        textStrokeWidth: labelStrokeWidth,
        labels: {
          total: {
            display: false
          },
          value: {
            display: (context) => isValueLabelVisible(context, options.showValueLabels) ? "auto" : false,
            anchor: "center",
            align: "top",
            offset: valueLabelOffset,
            formatter: (_value, context) => {
              const raw = getOriginalScatterPoint(context);
              if (!raw || typeof raw !== "object") return "";
              const xs = formatMeasureValue("x", raw.x, nullBandLabel);
              const ys = formatMeasureValue("y", raw.y, nullBandLabel);
              return `(${xs}, ${ys})`;
            }
          },
          caption: {
            display: (context) => isPointLabelVisible(context, options.showPointLabels) ? "auto" : false,
            anchor: "center",
            align: "top",
            offset: captionLabelOffset,
            formatter: (_value, context) => {
              const raw = getOriginalScatterPoint(context);
              return getPointCaption(raw) ?? "";
            }
          }
        }
      },
      legend: {
        display: options.showLegend
      },
      tooltip: {
        enabled: options.showTooltips,
        callbacks: {
          label: (ctx) => getScatterTooltipLabel(ctx, nullBandLabel, formatMeasureValue)
        }
      }
    },
    scales: {
      x: {
        // Match line charts: only the value axis (Y) uses log; X stays linear for correct point spread.
        type: "linear",
        grid: { display: false },
        ticks: {
          ...ticksDefault,
          callback: formatAxisTickValue
        },
        title: {
          display: Boolean(options.xAxisLabel),
          text: options.xAxisLabel
        },
        reverse: options.reverseXAxis,
        min: computeAxisMin2(
          options.xAxisRangeMin,
          applyNullX ? nullBand?.computedXAxisMin : void 0
        ),
        max: options.xAxisRangeMax
      },
      y: {
        type: options.showLogarithmicScale ? "logarithmic" : "linear",
        ...!options.showLogarithmicScale ? { grace: "5%" } : {},
        grid: { display: false },
        ticks: {
          ...ticksMuted,
          callback: formatAxisTickValue
        },
        title: {
          display: Boolean(options.yAxisLabel),
          text: options.yAxisLabel
        },
        min: computeAxisMin2(
          options.yAxisRangeMin,
          applyNullY ? nullBand?.computedYAxisMin : void 0
        ),
        max: options.yAxisRangeMax
      }
    }
  };
  return mergician6(getChartjsAxisOptions(), newOptions);
};
var getScatterChartOptions = (config) => {
  const { nullBand, nullBandLabel, showLogarithmicScale } = config;
  const options = mergician6(
    getScatterBaseOptions(config, { nullBand: nullBand ?? null, nullBandLabel }),
    getScatterChartAxisBorderPatch()
  );
  if (!nullBand || showLogarithmicScale) return options;
  const existingScales = options.scales ?? {};
  const scales = { ...existingScales };
  if (nullBand.hasNullX) {
    const { xNullPos } = nullBand;
    const xScale = existingScales.x ?? {};
    const prevCb = xScale.ticks?.callback;
    scales.x = {
      ...xScale,
      ticks: {
        ...xScale.ticks,
        callback: (tickValue) => {
          const v = typeof tickValue === "number" ? tickValue : Number(tickValue);
          if (Number.isFinite(v) && Math.abs(v - xNullPos) < 1e-9) return nullBandLabel;
          return prevCb?.(tickValue) ?? formatAxisTickValue(tickValue);
        }
      }
    };
  }
  if (nullBand.hasNullY) {
    const { yNullPos } = nullBand;
    const yScale = existingScales.y ?? {};
    const prevCb = yScale.ticks?.callback;
    scales.y = {
      ...yScale,
      ticks: {
        ...yScale.ticks,
        callback: (tickValue) => {
          const v = typeof tickValue === "number" ? tickValue : Number(tickValue);
          if (Number.isFinite(v) && Math.abs(v - yNullPos) < 1e-9) return nullBandLabel;
          return prevCb?.(tickValue) ?? formatAxisTickValue(tickValue);
        }
      }
    };
  }
  return { ...options, scales };
};

// src/components/charts/scatter/ScatterChart/ScatterChart.utils.ts
var getScatterChartData = (data, props) => {
  const dataForChart = props.showLogarithmicScale ? filterNumericScatterData(data) : data;
  return getScatterDataWithNullBand(dataForChart, {
    nullBand: props.nullBand,
    supportsNullMeasures: !props.showLogarithmicScale
  });
};

// src/components/charts/scatter/ScatterChart/ScatterChart.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
ChartJS3.register(
  ScatterController,
  LineElement2,
  PointElement2,
  LinearScale3,
  LogarithmicScale3,
  Title3,
  Tooltip3,
  Legend3,
  ChartDataLabels3,
  AnnotationPlugin2
);
var ScatterChart = ({
  options = {},
  data,
  onClick,
  nullBandLabel = "No value",
  ...props
}) => {
  const chartRef = useRef3(null);
  const propsWithNullBandLabel = { ...props, nullBandLabel };
  const nullBand = getScatterNullBand(data.datasets, props.showLogarithmicScale);
  const chartData = getScatterChartData(data, { ...propsWithNullBandLabel, nullBand });
  const scatterOptions = mergician7(
    getScatterChartOptions({ ...propsWithNullBandLabel, nullBand }),
    options
  );
  return /* @__PURE__ */ jsx3("div", { className: styles4.chartContainer, children: /* @__PURE__ */ jsx3(
    Scatter,
    {
      ref: chartRef,
      data: chartData,
      options: scatterOptions,
      plugins: getScatterChartPlugins(nullBand),
      onClick: buildChartjsOnClick(chartRef, onClick)
    }
  ) });
};

// src/components/charts/scatter/BubbleChart/BubbleChart.tsx
import {
  Chart as ChartJS4,
  LinearScale as LinearScale4,
  LogarithmicScale as LogarithmicScale4,
  PointElement as PointElement3,
  BubbleController,
  Title as Title4,
  Tooltip as Tooltip4,
  Legend as Legend4
} from "chart.js";
import { Bubble } from "react-chartjs-2";
import styles5 from "./charts.module-YNKCWRLU.module.css";
import { useRef as useRef4 } from "react";
import { mergician as mergician9 } from "mergician";
import ChartDataLabels4 from "chartjs-plugin-datalabels";
import AnnotationPlugin3 from "chartjs-plugin-annotation";

// src/components/charts/scatter/BubbleChart/BubbleChart.utils.ts
import { mergician as mergician8 } from "mergician";
var getMaxBubbleSize = (datasets) => {
  let max = 0;
  for (const ds of datasets) {
    for (const pt of ds.data) {
      if (!isMeasureMissing(pt.size) && pt.size > 0) {
        max = Math.max(max, pt.size);
      }
    }
  }
  return max;
};
var getBubbleRadius = (size, maxBubbleSize, bubbleMinRadiusPx, bubbleMaxRadiusPx) => {
  if (isMeasureMissing(size) || size <= 0 || maxBubbleSize <= 0)
    return bubbleMinRadiusPx;
  return Math.max(
    bubbleMinRadiusPx,
    Math.sqrt(size / maxBubbleSize) * bubbleMaxRadiusPx
  );
};
var getFormattedValue = (value, nullLabel) => {
  if (isMeasureMissing(value)) return nullLabel ?? "No value";
  return numberFormat.format(value);
};
var getBubbleTooltipLabel = (ctx, nullLabel) => {
  const ds = ctx.dataset;
  const orig = ds.originalData?.[ctx.dataIndex];
  const prefix = ds.label ? `${ds.label}: ` : "";
  const x = orig ? getFormattedValue(orig.x, nullLabel) : String(ctx.parsed.x);
  const y = orig ? getFormattedValue(orig.y, nullLabel) : String(ctx.parsed.y);
  const size = getFormattedValue(orig?.size, nullLabel);
  return `${prefix}(${x}, ${y}, ${size})`;
};
var getBubbleChartData = (data, props) => {
  const { nullBand, showLogarithmicScale, bubbleRadiusMin, bubbleRadiusMax } = props;
  const defaultOpacity = getStyleNumber("--em-bubblechart-point-opacity", "0.7");
  const nullOpacity = getStyleNumber("--em-bubblechart-point-opacity--null", "0.3");
  const bubbleMinRadiusPx = bubbleRadiusMin ?? getStyleNumber("--em-bubblechart-point-radius--min", "5");
  const bubbleMaxRadiusPx = bubbleRadiusMax ?? getStyleNumber("--em-bubblechart-point-radius--max", "40");
  const bubbleDataAsScatter = showLogarithmicScale ? filterNumericScatterData(data) : data;
  const scatterDataWithNullBand = getScatterDataWithNullBand(bubbleDataAsScatter, {
    nullBand,
    supportsNullMeasures: !showLogarithmicScale,
    opacityOverride: { defaultOpacity, nullOpacity }
  });
  const maxBubbleSize = getMaxBubbleSize(
    bubbleDataAsScatter.datasets
  );
  return {
    ...scatterDataWithNullBand,
    datasets: scatterDataWithNullBand.datasets.map((dataset) => {
      const originalBubbleData = dataset.originalData;
      const bubbleSizes = (originalBubbleData ?? []).map(
        (point) => getBubbleRadius(point.size, maxBubbleSize, bubbleMinRadiusPx, bubbleMaxRadiusPx)
      );
      return {
        ...dataset,
        clip: false,
        data: dataset.data.map((pt, i) => ({
          ...pt,
          r: bubbleSizes[i]
        })),
        bubbleSizes
      };
    })
  };
};
var getBubbleChartOptions = (config) => {
  const { nullBandLabel, bubbleRadiusMin } = config;
  const bubbleMinRadiusPx = bubbleRadiusMin ?? getStyleNumber("--em-bubblechart-point-radius--min", "5");
  const bubbleHoverScale = getStyleNumber("--em-bubblechart-point-radius--hover--scale", "1.2");
  const bubbleBorderWidth = getStyleNumber("--em-bubblechart-point-border-width", "1");
  const scatterBaseOptions = getScatterChartOptions(config);
  return mergician8(scatterBaseOptions, {
    scales: {
      x: { grace: 0 },
      y: { grace: 0 }
    },
    elements: {
      point: {
        borderWidth: bubbleBorderWidth,
        hoverRadius: ((ctx) => {
          const r = ctx.dataset.bubbleSizes[ctx.dataIndex] ?? bubbleMinRadiusPx;
          return r * (bubbleHoverScale - 1);
        })
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => getBubbleTooltipLabel(ctx, nullBandLabel)
        }
      }
    }
  });
};

// src/components/charts/scatter/BubbleChart/BubbleChart.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
ChartJS4.register(
  BubbleController,
  PointElement3,
  LinearScale4,
  LogarithmicScale4,
  Title4,
  Tooltip4,
  Legend4,
  ChartDataLabels4,
  AnnotationPlugin3
);
var BubbleChart = ({
  options = {},
  data,
  onClick,
  nullBandLabel = "No value",
  ...props
}) => {
  const chartRef = useRef4(null);
  const propsWithNullBandLabel = { ...props, nullBandLabel };
  const nullBand = getScatterNullBand(data.datasets, props.showLogarithmicScale);
  const chartData = getBubbleChartData(data, { ...propsWithNullBandLabel, nullBand });
  const bubbleOptions = mergician9(
    getBubbleChartOptions({ ...propsWithNullBandLabel, nullBand }),
    options
  );
  return /* @__PURE__ */ jsx4("div", { className: styles5.chartContainer, children: /* @__PURE__ */ jsx4(
    Bubble,
    {
      ref: chartRef,
      data: chartData,
      options: bubbleOptions,
      plugins: getScatterChartPlugins(nullBand),
      onClick: buildChartjsOnClick(chartRef, onClick)
    }
  ) });
};

// src/components/charts/chartjs.cartesian.utils.ts
import { Chart } from "chart.js";
var getChartPointClicked = (event) => {
  const chart = Chart.getChart(event.nativeEvent.target);
  if (!chart) return void 0;
  const points = chart.getElementsAtEventForMode(
    event.nativeEvent,
    "nearest",
    { intersect: true },
    false
  );
  const el = points[0];
  if (!el) return void 0;
  return { datasetIndex: el.datasetIndex, index: el.index };
};

// src/components/charts/kpis/KpiChart.tsx
import styles8 from "./KpiChart.module-2LUIN66C.module.css";

// src/components/charts/kpis/components/KpiChartChange.tsx
import styles7 from "./KpiChartChange.module-AVUDUMPZ.module.css";
import clsx2 from "clsx";

// src/components/shared/KpiTrend/KpiTrend.tsx
import { IconTrendingUp, IconTrendingDown } from "@tabler/icons-react";
import styles6 from "./KpiTrend.module-AZ56NYDA.module.css";
import clsx from "clsx";
import { jsx as jsx5, jsxs } from "react/jsx-runtime";
var KpiTrend = ({ value, reverseTrend = false, className }) => {
  const Icon = reverseTrend ? IconTrendingDown : IconTrendingUp;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: clsx(styles6.badge, reverseTrend ? styles6.negative : styles6.positive, className),
      children: [
        /* @__PURE__ */ jsx5(Icon, {}),
        /* @__PURE__ */ jsx5("span", { children: value })
      ]
    }
  );
};

// src/components/charts/kpis/components/KpiChartChange.tsx
import { Fragment, jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var KpiChartChange = ({
  value,
  comparisonValue = 0,
  showChangeAsPercentage,
  invertChangeColors = false,
  comparisonLabel,
  valueFormatter,
  percentageDecimalPlaces = 1,
  equalComparisonLabel,
  noPreviousDataLabel
}) => {
  const equalComparison = comparisonValue === value;
  const difference = value - comparisonValue;
  const isPositive = difference > 0;
  let differenceLabel;
  if (showChangeAsPercentage) {
    const percentage = difference / comparisonValue * 100;
    differenceLabel = `${percentage.toFixed(percentageDecimalPlaces)}%`;
  } else {
    differenceLabel = valueFormatter ? valueFormatter(difference) : difference.toString();
  }
  const displayValue = `${isPositive ? "+" : ""}${differenceLabel}`;
  const isBadTrend = !(isPositive !== invertChangeColors);
  const showNoPreviousData = showChangeAsPercentage && Number(comparisonValue) === 0;
  return /* @__PURE__ */ jsxs2("div", { className: styles7.kpiChangeContainerSizeGuide, children: [
    /* @__PURE__ */ jsxs2("div", { className: clsx2(styles7.kpiChartChangeContainer, styles7.hidden), children: [
      /* @__PURE__ */ jsx6(KpiTrend, { value: displayValue, reverseTrend: isBadTrend }),
      /* @__PURE__ */ jsx6("span", { className: styles7.kpiComparisonLabel, children: comparisonLabel })
    ] }),
    /* @__PURE__ */ jsx6("div", { className: styles7.kpiAbsoluteContainer, children: /* @__PURE__ */ jsx6("div", { className: styles7.kpiChartChangeContainer, children: showNoPreviousData ? /* @__PURE__ */ jsx6("span", { className: styles7.kpiComparisonLabel, children: noPreviousDataLabel }) : /* @__PURE__ */ jsxs2(Fragment, { children: [
      !equalComparison && /* @__PURE__ */ jsx6(KpiTrend, { value: displayValue, reverseTrend: isBadTrend }),
      /* @__PURE__ */ jsx6("span", { className: styles7.kpiComparisonLabel, children: equalComparison ? equalComparisonLabel ?? comparisonLabel : comparisonLabel })
    ] }) }) })
  ] });
};

// src/components/charts/kpis/KpiChart.tsx
import { AutoTextSize } from "auto-text-size";

// src/components/shared/ConditionalWrapper/ConditionalWrapper.tsx
import { Fragment as Fragment2, jsx as jsx7 } from "react/jsx-runtime";
var ConditionalWrapper = ({ condition, wrapper, children }) => condition ? wrapper(children) : /* @__PURE__ */ jsx7(Fragment2, { children });
var ConditionalWrapper_default = ConditionalWrapper;

// src/components/charts/kpis/KpiChart.tsx
import { jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var getKpiDisplayValue = ({
  value,
  displayNullAs,
  valueFormatter
}) => {
  if (value == null) {
    if (displayNullAs === "") return "";
    const numericDisplayNullAs = Number(displayNullAs);
    return Number.isNaN(numericDisplayNullAs) ? displayNullAs : valueFormatter?.(numericDisplayNullAs) ?? numericDisplayNullAs;
  }
  return valueFormatter?.(value) ?? value;
};
var KpiChart = ({
  value,
  valueFontSize,
  trendFontSize,
  comparisonValue,
  comparisonLabel,
  invertChangeColors,
  showChangeAsPercentage,
  percentageDecimalPlaces = 1,
  equalComparisonLabel = "No change",
  noPreviousDataLabel = "No previous data",
  displayNullAs = "",
  valueFormatter
}) => {
  const hasComparisonValue = comparisonValue !== void 0 && value != null;
  const displayValue = getKpiDisplayValue({ value, displayNullAs, valueFormatter });
  const autoResizeValueFontSize = !valueFontSize;
  return /* @__PURE__ */ jsxs3("div", { className: styles8.kpiChartContainer, children: [
    /* @__PURE__ */ jsx8("div", { className: styles8.kpiChartValueContainer, children: /* @__PURE__ */ jsx8(
      ConditionalWrapper_default,
      {
        condition: autoResizeValueFontSize,
        wrapper: (children) => /* @__PURE__ */ jsx8(
          AutoTextSize,
          {
            mode: "boxoneline",
            minFontSizePx: getStyleNumber("--em-kpichart-number-font-size--min", "12px"),
            maxFontSizePx: getStyleNumber("--em-kpichart-number-font-size--max", "999px"),
            children
          }
        ),
        children: /* @__PURE__ */ jsx8("h2", { title: displayValue?.toString(), style: { fontSize: valueFontSize }, children: displayValue })
      }
    ) }),
    hasComparisonValue && /* @__PURE__ */ jsx8("div", { className: styles8.kpiComparisonContainer, style: { fontSize: trendFontSize }, children: /* @__PURE__ */ jsx8("div", { style: { visibility: hasComparisonValue ? "visible" : "hidden" }, children: /* @__PURE__ */ jsx8(
      KpiChartChange,
      {
        equalComparisonLabel,
        comparisonLabel,
        comparisonValue,
        invertChangeColors,
        percentageDecimalPlaces,
        showChangeAsPercentage,
        value,
        valueFormatter,
        noPreviousDataLabel
      }
    ) }) })
  ] });
};

// src/components/charts/pies/DonutChart/DonutChart.tsx
import { useRef as useRef6 } from "react";
import { Pie } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS5, Legend as Legend5, Tooltip as Tooltip5 } from "chart.js";
import ChartDataLabels5 from "chartjs-plugin-datalabels";
import AnnotationPlugin4 from "chartjs-plugin-annotation";

// src/components/charts/pies/pies.utils.ts
import { mergician as mergician10 } from "mergician";
var getPieChartData = (data) => {
  const chartColors = getChartColors();
  const mergedData = {
    ...data,
    datasets: data.datasets?.map((dataset) => {
      const colors = dataset.data.map((_value, index) => chartColors[index % chartColors.length]);
      const defaultDataset = {
        backgroundColor: colors,
        borderColor: colors
      };
      const merged = mergician10(defaultDataset, dataset);
      return merged;
    }) || []
  };
  return mergedData;
};
var getPieChartOptions = (options) => {
  const pieChartOptions = {
    plugins: {
      legend: { display: options.showLegend },
      datalabels: {
        display: options.showValueLabels ? "auto" : false
      },
      tooltip: {
        enabled: options.showTooltips
      }
    }
  };
  return mergician10(getChartjsOptions(), pieChartOptions);
};
var getDonutChartOptions = (options) => {
  const donutChartOptions = {
    cutout: "60%",
    elements: {
      arc: {
        borderWidth: 1
      }
    },
    plugins: {
      annotation: {
        annotations: {
          innerlabel: {
            content: [options.label, options.subLabel].filter(Boolean),
            type: "doughnutLabel",
            font: [
              {
                size: getStyleNumber("--em-piechart-donut-number-line-height", "2.375rem"),
                weight: getStyleNumber("--em-piechart-donut-number-font-weight", "700"),
                height: getStyleNumber("--em-piechart-donut-number-line-height", "2.375rem"),
                family: getStyle("--em-piechart-font-family", "sans-serif")
              },
              {
                size: getStyleNumber("--em-piechart-donut-label-font-size", "1rem"),
                weight: getStyleNumber("--em-piechart-donut-label-font-weight", "500"),
                height: getStyleNumber("--em-piechart-donut-label-line-height", "1rem"),
                family: getStyle("--em-piechart-font-family", "sans-serif")
              }
            ],
            color: [getStyle("--em-piechart-donut-color", "#212129")]
          }
          // According to the last comment, a fix should be coming in the next release:
          // https://github.com/chartjs/chartjs-plugin-annotation/commit/1e95744fb98e6fe9426f8b6a7bd17b1fcdee2f42
        }
      }
    }
  };
  return mergician10(getPieChartOptions(options), donutChartOptions);
};

// src/components/charts/pies/DonutChart/DonutChart.tsx
import { mergician as mergician11 } from "mergician";
import styles9 from "./charts.module-YNKCWRLU.module.css";

// src/hooks/useResizeObserver.hook.ts
import { useLayoutEffect, useRef as useRef5, useState } from "react";
var useResizeObserver = (elRef, timeout = 100) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const timeoutRef = useRef5(void 0);
  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const updateSize = (rect) => {
      setSize({
        width: Math.max(0, rect.width),
        height: Math.max(0, rect.height)
      });
    };
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        updateSize(entry.contentRect);
      }, timeout);
    });
    ro.observe(el);
    updateSize(el.getBoundingClientRect());
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      ro.disconnect();
    };
  }, [elRef, timeout]);
  return size;
};

// src/components/charts/pies/DonutChart/DonutChart.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var MIN_WIDTH_HEIGHT_TO_SHOW_CHART = 10;
ChartJS5.register(ArcElement, Tooltip5, Legend5, ChartDataLabels5, AnnotationPlugin4);
var DonutChart = ({
  label,
  subLabel,
  options = {},
  data,
  onClick,
  showLegend = true,
  showTooltips = true,
  showValueLabels = true
}) => {
  const chartRef = useRef6(null);
  const containerRef = useRef6(null);
  const donutLabelOptions = mergician11(
    getDonutChartOptions({ showLegend, showTooltips, showValueLabels, label, subLabel }),
    options
  );
  const { height, width } = useResizeObserver(containerRef, 0);
  const hideChart = height < MIN_WIDTH_HEIGHT_TO_SHOW_CHART || width < MIN_WIDTH_HEIGHT_TO_SHOW_CHART;
  return /* @__PURE__ */ jsx9("div", { className: styles9.chartContainer, ref: containerRef, children: hideChart ? null : /* @__PURE__ */ jsx9(
    Pie,
    {
      ref: chartRef,
      data: getPieChartData(data),
      options: donutLabelOptions,
      onClick: buildChartjsOnClick(chartRef, onClick)
    }
  ) });
};

// src/components/charts/pies/PieChart/PieChart.tsx
import { useRef as useRef7 } from "react";
import { Pie as Pie2 } from "react-chartjs-2";
import { ArcElement as ArcElement2, Chart as ChartJS6, Legend as Legend6, Tooltip as Tooltip6 } from "chart.js";
import ChartDataLabels6 from "chartjs-plugin-datalabels";
import { mergician as mergician12 } from "mergician";
import styles10 from "./charts.module-YNKCWRLU.module.css";
import AnnotationPlugin5 from "chartjs-plugin-annotation";
import { jsx as jsx10 } from "react/jsx-runtime";
ChartJS6.register(ArcElement2, Tooltip6, Legend6, ChartDataLabels6, AnnotationPlugin5);
var PieChart = ({
  data,
  options = {},
  onClick,
  showLegend = true,
  showTooltips = true,
  showValueLabels = true
}) => {
  const chartRef = useRef7(null);
  const pieOptions = mergician12(
    getPieChartOptions({
      showLegend,
      showTooltips,
      showValueLabels
    }),
    options
  );
  return /* @__PURE__ */ jsx10("div", { className: styles10.chartContainer, children: /* @__PURE__ */ jsx10(
    Pie2,
    {
      ref: chartRef,
      data: getPieChartData(data),
      options: pieOptions,
      onClick: buildChartjsOnClick(chartRef, onClick)
    }
  ) });
};

// src/components/charts/tables/HeatMap/HeatMap.tsx
import { useMemo, useCallback } from "react";
import tableStyles from "./tables.module-6REGLCSQ.module.css";
import clsx3 from "clsx";

// src/components/charts/tables/HeatMap/HeatMap.utils.ts
var clamp01 = (x) => x < 0 ? 0 : x > 1 ? 1 : x;
var easeOutQuad = (x) => 1 - (1 - x) * (1 - x);
var lerp = (a, b, t) => a + (b - a) * t;
var OUTSIDE_DARKEN_MAX = 0.35;
var LERP_DARKEST_COLOR = "#000000";
var LERP_LIGHTEST_COLOR = "#FFFFFF";
var idOf = (v) => String(v ?? "");
var CSS_RGB_REGEX = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i;
var COLOR_CACHE = /* @__PURE__ */ new Map();
var normalizeHex = (hex) => {
  if (!hex || hex[0] !== "#") return hex;
  const v = hex.slice(1).toLowerCase();
  if (v.length === 3) return `#${v[0]}${v[0]}${v[1]}${v[1]}${v[2]}${v[2]}`;
  if (v.length === 6) return `#${v}`;
  return hex.toLowerCase();
};
var resolveCssColorToHex = (input) => {
  if (!input) return input;
  const cached = COLOR_CACHE.get(input);
  if (cached) return cached;
  if (input.startsWith("#")) {
    const hex2 = normalizeHex(input);
    COLOR_CACHE.set(input, hex2);
    return hex2;
  }
  if (typeof document === "undefined") {
    return input;
  }
  const el = document.createElement("span");
  el.style.color = input;
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color || "";
  el.remove();
  const m = computed.match(CSS_RGB_REGEX);
  if (!m) return input;
  const [, r = "0", g = "0", b = "0"] = m;
  const toHex = (n) => Number(n).toString(16).padStart(2, "0");
  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase();
  COLOR_CACHE.set(input, hex);
  return hex;
};
var hexToRgb = (color) => {
  const hex = normalizeHex(resolveCssColorToHex(color)).slice(1);
  if (hex.length !== 6) return { r: 0, g: 0, b: 0 };
  const i = parseInt(hex, 16);
  return { r: i >> 16 & 255, g: i >> 8 & 255, b: i & 255 };
};
var rgbToHex = (r, g, b) => {
  const to = (x) => Math.round(x).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
};
var lerpColor = (a, b, t) => {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex(lerp(A.r, B.r, t), lerp(A.g, B.g, t), lerp(A.b, B.b, t));
};
var getBrightness = (color) => {
  const { r, g, b } = hexToRgb(color);
  return (r * 299 + g * 587 + b * 114) / 1e3;
};
var getCellColor = (backgroundColor) => {
  return getBrightness(backgroundColor) < 150 ? "#FFFFFF" : "#212129";
};
var makeDiverging = (minC, midC, maxC, midpoint = 0.5) => {
  const m = clamp01(midpoint);
  return (t) => {
    const x = clamp01(t);
    return x <= m ? lerpColor(minC, midC, m === 0 ? 1 : x / m) : lerpColor(midC, maxC, (x - m) / (1 - m));
  };
};
var parsePercentString = (s) => {
  const t = s.trim();
  if (!t.endsWith("%")) return null;
  const n = Number.parseFloat(t.slice(0, -1));
  return Number.isFinite(n) ? clamp01(n / 100) : null;
};
var thresholdToRaw = (t, rawMin, rawMax, fallback) => {
  if (t == null) return fallback;
  if (typeof t === "number" && Number.isFinite(t)) return t;
  const asPct = typeof t === "string" ? parsePercentString(t) : null;
  if (asPct != null) return rawMin + asPct * (rawMax - rawMin);
  const maybe = Number(t);
  return Number.isFinite(maybe) ? maybe : fallback;
};
var toFiniteOrSelf = (v) => {
  if (v == null) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
};
var getCellValue = (value, displayNullAs) => {
  const fallback = displayNullAs && Number.isFinite(Number(displayNullAs)) ? Number(displayNullAs) : displayNullAs;
  return toFiniteOrSelf(value ?? fallback);
};
var getCellBackground = (value, colorForValue) => {
  if (typeof value !== "number" || !Number.isFinite(value))
    return getStyle("--em-tablechart-cell-background", "#fff");
  return colorForValue(value);
};
var getCellDisplayValue = (value, showValues, measure) => {
  if (!showValues) return null;
  if (typeof value === "string" || value == null || Number.isNaN(value)) return value;
  return measure?.format ? measure.format(Number(value)) : value;
};
var getMidPoint = (domainMin, domainMax) => {
  const range = domainMax - domainMin;
  if (range === 0) return 0.5;
  return ((domainMin + domainMax) / 2 - domainMin) / range;
};
var createColorForValue = ({
  domainMin,
  domainMax,
  rawMin,
  rawMax,
  minColor,
  midColor,
  maxColor
}) => {
  minColor = minColor ?? lerpColor(midColor, LERP_LIGHTEST_COLOR, 0.5);
  maxColor = maxColor ?? lerpColor(midColor, LERP_DARKEST_COLOR, 0.5);
  const midPoint = getMidPoint(domainMin, domainMax);
  const scale = makeDiverging(minColor, midColor, maxColor, midPoint);
  const leftTailLen = Math.max(1e-6, domainMin - rawMin);
  const rightTailLen = Math.max(1e-6, rawMax - domainMax);
  const domRange = domainMax - domainMin;
  if (domRange === 0) {
    const mid = scale(0.5);
    return (_v) => mid;
  }
  return (v) => {
    if (!Number.isFinite(v)) return scale(0.5);
    const t = (v - domainMin) / domRange;
    if (t <= 0) {
      const dist = Math.max(0, domainMin - v) / leftTailLen;
      const strength = easeOutQuad(clamp01(dist)) * OUTSIDE_DARKEN_MAX;
      return strength > 0 ? lerpColor(minColor, LERP_LIGHTEST_COLOR, strength) : minColor;
    }
    if (t >= 1) {
      const dist = Math.max(0, v - domainMax) / rightTailLen;
      const strength = easeOutQuad(clamp01(dist)) * OUTSIDE_DARKEN_MAX;
      return strength > 0 ? lerpColor(maxColor, LERP_DARKEST_COLOR, strength) : maxColor;
    }
    return scale(t);
  };
};

// src/components/charts/tables/tables.utils.ts
var getTableCellWidthStyle = (width) => {
  return {
    minWidth: width ? `${width}px` : "unset",
    maxWidth: width ? `${width}px` : "unset",
    width: width ? `${width}px` : "unset"
  };
};

// src/components/charts/tables/HeatMap/HeatMap.tsx
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
var HeatMap = ({
  data,
  showValues = false,
  className,
  columnDimension,
  rowDimension,
  measure,
  minThreshold,
  maxThreshold,
  minColor,
  midColor = getStyle("--em-tablechart-heatmap-color", "#FF5400"),
  maxColor,
  columnWidth,
  firstColumnWidth,
  displayNullAs,
  onCellClick
}) => {
  const { rawMin, rawMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const d of data) {
      const value = getCellValue(d[measure.key], displayNullAs);
      if (value != null && typeof value === "number" && Number.isFinite(value)) {
        if (value < min) min = value;
        if (value > max) max = value;
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { rawMin: 0, rawMax: 0 };
    return { rawMin: min, rawMax: max };
  }, [data, measure.key, displayNullAs]);
  const { domainMin, domainMax } = useMemo(() => {
    const domMin = thresholdToRaw(minThreshold, rawMin, rawMax, rawMin);
    const domMax = thresholdToRaw(maxThreshold, rawMin, rawMax, rawMax);
    return domMin <= domMax ? { domainMin: domMin, domainMax: domMax } : { domainMin: domMax, domainMax: domMin };
  }, [minThreshold, maxThreshold, rawMin, rawMax]);
  const columnValues = useMemo(
    () => Array.from(
      new Set(
        data.map((d) => d[columnDimension.key]).filter((v) => v != null).map(idOf)
      )
    ),
    [data, columnDimension.key]
  );
  const rowValues = useMemo(
    () => Array.from(
      new Set(
        data.map((d) => d[rowDimension.key]).filter((v) => v != null).map(idOf)
      )
    ),
    [data, rowDimension.key]
  );
  const cellMap = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const d of data) {
      const r = idOf(d[rowDimension.key]);
      const c = idOf(d[columnDimension.key]);
      if (!map.has(r)) map.set(r, /* @__PURE__ */ new Map());
      map.get(r).set(c, d);
    }
    return map;
  }, [data, rowDimension.key, columnDimension.key]);
  const colorForValue = useCallback(
    (v) => {
      return createColorForValue({
        domainMin,
        domainMax,
        rawMin,
        rawMax,
        minColor,
        midColor,
        maxColor
      })(v);
    },
    [domainMin, domainMax, rawMin, rawMax, minColor, midColor, maxColor]
  );
  return /* @__PURE__ */ jsx11("div", { className: clsx3(tableStyles.tableFullContainer, className), children: /* @__PURE__ */ jsx11(
    "div",
    {
      className: clsx3(
        tableStyles.tableAdjustedContainer,
        (!columnWidth || !firstColumnWidth) && tableStyles.fullWidth
      ),
      children: /* @__PURE__ */ jsxs4("table", { className: tableStyles.table, "aria-label": "Heat map", children: [
        /* @__PURE__ */ jsx11("thead", { children: /* @__PURE__ */ jsxs4("tr", { children: [
          /* @__PURE__ */ jsx11(
            "th",
            {
              className: tableStyles.stickyFirstColumn,
              style: getTableCellWidthStyle(firstColumnWidth),
              children: measure.label
            }
          ),
          columnValues.map((cv, index) => /* @__PURE__ */ jsx11("th", { style: getTableCellWidthStyle(columnWidth), children: columnDimension.format ? columnDimension.format(cv) : cv }, `col-${cv}-${index}`))
        ] }) }),
        /* @__PURE__ */ jsx11("tbody", { children: rowValues.map((rv) => /* @__PURE__ */ jsxs4("tr", { children: [
          /* @__PURE__ */ jsx11(
            "th",
            {
              scope: "row",
              className: tableStyles.stickyFirstColumn,
              style: getTableCellWidthStyle(firstColumnWidth),
              children: rowDimension.format ? rowDimension.format(rv) : rv
            }
          ),
          columnValues.map((cv) => {
            const obj = cellMap.get(rv)?.get(cv);
            const value = getCellValue(obj?.[measure.key], displayNullAs);
            const background = getCellBackground(value, colorForValue);
            const color = getCellColor(background);
            return /* @__PURE__ */ jsx11(
              "td",
              {
                style: {
                  background,
                  color,
                  cursor: onCellClick ? "pointer" : void 0,
                  ...getTableCellWidthStyle(columnWidth)
                },
                onClick: onCellClick ? () => onCellClick({
                  rowDimensionValue: rv,
                  columnDimensionValue: cv
                }) : void 0,
                children: getCellDisplayValue(value, showValues, measure)
              },
              `cell-${rv}-${cv}`
            );
          })
        ] }, `row-${rv}`)) })
      ] })
    }
  ) });
};

// src/components/charts/tables/PivotTable/PivotTable.tsx
import { Fragment as Fragment3, useMemo as useMemo3, useState as useState3 } from "react";
import tableStyles2 from "./tables.module-6REGLCSQ.module.css";
import clsx4 from "clsx";
import { IconLoader2, IconChevronDown, IconChevronRight } from "@tabler/icons-react";

// src/components/charts/tables/PivotTable/PivotTable.utils.ts
import { useEffect, useMemo as useMemo2, useState as useState2 } from "react";
var isNumber = (v) => typeof v === "number" && !Number.isNaN(v);
var getPercentageDisplay = (percentage, percentageDecimalPlaces) => {
  return `${percentage.toFixed(percentageDecimalPlaces)}%`;
};
var getCellDisplayValue2 = (value, measure, dataObject, denominatorTotal) => {
  if (measure.showAsPercentage) {
    if (isNumber(Number(value)) && isNumber(denominatorTotal) && denominatorTotal > 0) {
      return getPercentageDisplay(
        value / denominatorTotal * 100,
        measure.percentageDecimalPlaces ?? 0
      );
    }
  }
  return measure.accessor ? measure.accessor(dataObject) : value;
};
var buildCellMap = (rows, rowKey, colKey) => {
  const map = /* @__PURE__ */ new Map();
  for (const d of rows) {
    const r = String(d[rowKey]);
    const c = String(d[colKey]);
    if (!map.has(r)) map.set(r, /* @__PURE__ */ new Map());
    map.get(r).set(c, d);
  }
  return map;
};
var getMeasureTotal = (totalsMap, key, measureIndex, measuresCount) => {
  const totals = totalsMap.get(key) ?? Array(measuresCount).fill(0);
  return measureIndex >= 0 ? totals[measureIndex] ?? 0 : 0;
};
var computeSubRowTotal = (cellMap, rowKey, columnValues, measureKey) => {
  let total = 0;
  for (const columnValue of columnValues) {
    const data = cellMap.get(rowKey)?.get(String(columnValue)) ?? {};
    const val = Number(data?.[measureKey]);
    if (!Number.isNaN(val)) total += val;
  }
  return total;
};
var useProgressiveRows = (rowValues, options) => {
  const { progressive, batchSize, batchDelayMs } = options;
  const [visibleCount, setVisibleCount] = useState2(
    () => progressive ? Math.min(batchSize, rowValues.length) : rowValues.length
  );
  useEffect(() => {
    if (!progressive) {
      setVisibleCount(rowValues.length);
      return;
    }
    let cancelled = false;
    let t = null;
    setVisibleCount(Math.min(batchSize, rowValues.length));
    const tick = () => {
      setVisibleCount((prev) => {
        const next = Math.min(prev + batchSize, rowValues.length);
        if (next < rowValues.length && !cancelled) {
          t = window.setTimeout(tick, batchDelayMs);
        }
        return next;
      });
    };
    t = window.setTimeout(tick, batchDelayMs);
    return () => {
      cancelled = true;
      if (t !== null) window.clearTimeout(t);
    };
  }, [progressive, batchSize, batchDelayMs, rowValues.length]);
  return progressive ? rowValues.slice(0, visibleCount) : rowValues;
};
var usePivotTotals = (data, measures, rowDimensionKey, columnDimensionKey, columnValues, rowValues) => {
  return useMemo2(() => {
    const cTotals = /* @__PURE__ */ new Map();
    const rTotals = /* @__PURE__ */ new Map();
    const gTotals = measures.map(() => 0);
    for (const d of data) {
      const r = String(d[rowDimensionKey]);
      const c = String(d[columnDimensionKey]);
      const cArr = cTotals.get(c) ?? measures.map(() => 0);
      const rArr = rTotals.get(r) ?? measures.map(() => 0);
      measures.forEach((m, i) => {
        const raw = d?.[m.key];
        const v = Number(raw);
        if (!Number.isNaN(v)) {
          cArr[i] += v;
          rArr[i] += v;
          gTotals[i] += v;
        }
      });
      cTotals.set(c, cArr);
      rTotals.set(r, rArr);
    }
    for (const c of columnValues) {
      if (!cTotals.has(String(c)))
        cTotals.set(
          String(c),
          measures.map(() => 0)
        );
    }
    for (const r of rowValues) {
      if (!rTotals.has(String(r)))
        rTotals.set(
          String(r),
          measures.map(() => 0)
        );
    }
    return { colTotals: cTotals, rowTotals: rTotals, grandTotals: gTotals };
  }, [data, measures, rowDimensionKey, columnDimensionKey, columnValues, rowValues]);
};

// src/components/charts/tables/PivotTable/PivotTable.tsx
import { Fragment as Fragment4, jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
var PivotTable = ({
  columnWidth,
  firstColumnWidth,
  data,
  measures,
  rowDimension,
  columnDimension,
  progressive = true,
  batchSize = 100,
  batchDelayMs = 0,
  rowTotalsFor = [],
  columnTotalsFor = [],
  totalLabel = "Total",
  className,
  expandableRows = false,
  subRowsByRow,
  loadingRows,
  onRowExpand,
  subRowDimension
}) => {
  const effectiveSubRowDimension = subRowDimension ?? rowDimension;
  const rowValues = useMemo3(() => {
    const s = /* @__PURE__ */ new Set();
    for (const d of data) {
      const rowValue = d[rowDimension.key];
      if (rowValue != null) s.add(rowValue);
    }
    return Array.from(s);
  }, [data, rowDimension.key]);
  const columnValues = useMemo3(() => {
    const s = /* @__PURE__ */ new Set();
    for (const d of data) {
      const columnValue = d[columnDimension.key];
      if (columnValue != null) s.add(columnValue);
    }
    return Array.from(s);
  }, [data, columnDimension.key]);
  const cellMap = useMemo3(
    () => buildCellMap(data, rowDimension.key, columnDimension.key),
    [data, rowDimension.key, columnDimension.key]
  );
  const rowTotalsSet = useMemo3(() => new Set(rowTotalsFor), [rowTotalsFor]);
  const columnTotalsSet = useMemo3(() => new Set(columnTotalsFor), [columnTotalsFor]);
  const hasRowTotals = rowTotalsSet.size > 0;
  const hasColumnTotals = columnTotalsSet.size > 0;
  const measureIndexByKey = useMemo3(() => {
    const map = /* @__PURE__ */ new Map();
    measures.forEach((m, i) => map.set(String(m.key), i));
    return map;
  }, [measures]);
  const { colTotals, rowTotals, grandTotals } = usePivotTotals(
    data,
    measures,
    rowDimension.key,
    columnDimension.key,
    columnValues,
    rowValues
  );
  const [expandedRows, setExpandedRows] = useState3(() => /* @__PURE__ */ new Set());
  const handleRowExpand = (rowKey) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      const wasExpanded = next.has(rowKey);
      if (wasExpanded) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
        if (!subRowsByRow?.has(rowKey)) {
          onRowExpand?.(rowKey);
        }
      }
      return next;
    });
  };
  const visibleRows = useProgressiveRows(rowValues, {
    progressive,
    batchSize,
    batchDelayMs
  });
  const renderMeasureCells = (rowCellMap, rowKey, keyPrefix) => columnValues.flatMap(
    (columnValue) => measures.map((measure, idx) => {
      const object = rowCellMap.get(rowKey)?.get(String(columnValue)) ?? {};
      const value = object?.[measure.key];
      const key = `${keyPrefix}-${columnValue}-${measure.key}-${idx}`;
      const mi = measureIndexByKey.get(String(measure.key)) ?? -1;
      const colTotal = getMeasureTotal(colTotals, String(columnValue), mi, measures.length);
      const displayValue = getCellDisplayValue2(value, measure, object, colTotal);
      return /* @__PURE__ */ jsx12("td", { title: displayValue, children: displayValue }, key);
    })
  );
  const renderRowTotalCells = (getTotalValue, keyPrefix) => {
    if (!hasRowTotals) return null;
    return measures.filter((measure) => rowTotalsSet.has(measure.key)).map((measure, idx) => {
      const measureIndex = measureIndexByKey.get(measure.key) ?? -1;
      const key = `${keyPrefix}-${measure.key}-${idx}`;
      const value = getTotalValue(measure.key, measureIndex);
      const displayValue = getCellDisplayValue2(
        value,
        measure,
        { [measure.key]: value },
        grandTotals[measureIndex] || 1
      );
      return /* @__PURE__ */ jsx12("td", { className: tableStyles2.boltCell, title: displayValue, children: displayValue }, key);
    });
  };
  const renderSubRows = (rowKey, subRows) => {
    const subRowValues = Array.from(
      new Set(subRows.map((sr) => sr[effectiveSubRowDimension.key]).filter((v) => v != null))
    );
    const subRowCellMap = buildCellMap(subRows, effectiveSubRowDimension.key, columnDimension.key);
    return subRowValues.map((subRowValue, subRowIdx) => {
      const subRowDimensionValue = effectiveSubRowDimension.formatValue ? effectiveSubRowDimension.formatValue(subRowValue) : subRowValue;
      return /* @__PURE__ */ jsxs5("tr", { className: tableStyles2.subRow, children: [
        /* @__PURE__ */ jsx12(
          "th",
          {
            scope: "row",
            className: clsx4(tableStyles2.stickyFirstColumn, tableStyles2.subRowFirstColumn),
            style: getTableCellWidthStyle(firstColumnWidth),
            title: subRowDimensionValue,
            children: /* @__PURE__ */ jsx12("span", { children: subRowDimensionValue })
          }
        ),
        renderMeasureCells(
          subRowCellMap,
          String(subRowValue),
          `sub-cell-${rowKey}-${subRowIdx}`
        ),
        renderRowTotalCells(
          (key) => computeSubRowTotal(subRowCellMap, String(subRowValue), columnValues, key),
          `sub-total-${rowKey}-${subRowIdx}`
        )
      ] }, `sub-${rowKey}-${subRowIdx}`);
    });
  };
  return /* @__PURE__ */ jsx12("div", { className: clsx4(tableStyles2.tableFullContainer, className), children: /* @__PURE__ */ jsx12(
    "div",
    {
      className: clsx4(
        tableStyles2.tableAdjustedContainer,
        (!columnWidth || !firstColumnWidth) && tableStyles2.fullWidth
      ),
      children: /* @__PURE__ */ jsxs5(
        "table",
        {
          className: tableStyles2.table,
          "aria-label": `${rowDimension.label} by ${columnDimension.label}`,
          children: [
            /* @__PURE__ */ jsxs5("thead", { children: [
              /* @__PURE__ */ jsxs5("tr", { children: [
                /* @__PURE__ */ jsx12(
                  "th",
                  {
                    scope: "col",
                    rowSpan: 1,
                    title: columnDimension.label,
                    className: tableStyles2.stickyFirstColumn,
                    style: getTableCellWidthStyle(firstColumnWidth),
                    children: columnDimension.label
                  }
                ),
                columnValues.map((columnValue) => {
                  const columnValueDisplay = columnDimension.formatValue ? columnDimension.formatValue(columnValue) : columnValue;
                  return /* @__PURE__ */ jsx12(
                    "th",
                    {
                      scope: "colgroup",
                      colSpan: measures.length,
                      title: columnValueDisplay,
                      children: columnValueDisplay
                    },
                    `col-${columnValue}`
                  );
                }),
                hasRowTotals && /* @__PURE__ */ jsx12(
                  "th",
                  {
                    scope: "colgroup",
                    colSpan: Array.from(rowTotalsSet).length,
                    className: tableStyles2.boltCell,
                    title: totalLabel,
                    children: totalLabel
                  },
                  "col-total-group"
                )
              ] }),
              /* @__PURE__ */ jsxs5("tr", { children: [
                /* @__PURE__ */ jsx12(
                  "th",
                  {
                    scope: "col",
                    rowSpan: 1,
                    title: rowDimension.label,
                    className: tableStyles2.stickyFirstColumn,
                    style: getTableCellWidthStyle(firstColumnWidth),
                    children: rowDimension.label
                  }
                ),
                columnValues.flatMap(
                  (col) => measures.map((measure, idx) => /* @__PURE__ */ jsx12(
                    "th",
                    {
                      scope: "col",
                      title: measure.label,
                      style: getTableCellWidthStyle(columnWidth),
                      children: measure.label
                    },
                    `sub-${String(col)}-${measure.key}-${idx}`
                  ))
                ),
                hasRowTotals && measures.filter((measure) => rowTotalsSet.has(measure.key)).map((measure, idx) => /* @__PURE__ */ jsx12(
                  "th",
                  {
                    scope: "col",
                    className: tableStyles2.boltCell,
                    title: measure.label,
                    style: getTableCellWidthStyle(columnWidth),
                    children: measure.label
                  },
                  `sub-total-${measure.key}-${idx}`
                ))
              ] })
            ] }),
            /* @__PURE__ */ jsxs5("tbody", { children: [
              visibleRows.map((row) => {
                const rowKey = String(row);
                const isExpanded = expandedRows.has(rowKey);
                const isLoading = loadingRows?.has(rowKey) ?? false;
                const subRows = subRowsByRow?.get(rowKey) ?? [];
                const hasLoadedSubRows = subRows.length > 0;
                const rowDimensionValue = rowDimension.formatValue ? rowDimension.formatValue(row) : row;
                const ariaLabel = expandableRows ? isLoading ? "Loading" : isExpanded ? `Collapse ${rowDimensionValue}` : `Expand ${rowDimensionValue}` : "";
                const showSubRows = isExpanded && !isLoading && hasLoadedSubRows;
                return /* @__PURE__ */ jsxs5(Fragment3, { children: [
                  /* @__PURE__ */ jsxs5(
                    "tr",
                    {
                      className: clsx4(isExpanded && tableStyles2.expandedRow),
                      title: rowDimensionValue,
                      children: [
                        /* @__PURE__ */ jsx12(
                          "th",
                          {
                            scope: "row",
                            title: rowDimensionValue,
                            className: clsx4(
                              tableStyles2.stickyFirstColumn,
                              expandableRows && tableStyles2.expandableRowCell
                            ),
                            onClick: () => expandableRows && handleRowExpand(rowKey),
                            onKeyDown: (e) => {
                              if (expandableRows && (e.key === "Enter" || e.key === " ")) {
                                e.preventDefault();
                                handleRowExpand(rowKey);
                              }
                            },
                            tabIndex: expandableRows ? 0 : void 0,
                            role: expandableRows ? "button" : void 0,
                            "aria-expanded": expandableRows ? isExpanded : void 0,
                            style: getTableCellWidthStyle(firstColumnWidth),
                            "aria-label": ariaLabel,
                            children: /* @__PURE__ */ jsxs5("span", { className: tableStyles2.firstColumnCell, children: [
                              expandableRows && /* @__PURE__ */ jsx12(Fragment4, { children: isLoading ? /* @__PURE__ */ jsx12(IconLoader2, { className: tableStyles2.subRowLoading }) : isExpanded ? /* @__PURE__ */ jsx12(IconChevronDown, {}) : /* @__PURE__ */ jsx12(IconChevronRight, {}) }),
                              /* @__PURE__ */ jsx12("span", { children: rowDimensionValue })
                            ] })
                          }
                        ),
                        renderMeasureCells(cellMap, String(row), `cell-${row}`),
                        renderRowTotalCells(
                          (_key, mi) => getMeasureTotal(rowTotals, String(row), mi, measures.length),
                          `row-total-${String(row)}`
                        )
                      ]
                    }
                  ),
                  showSubRows && renderSubRows(rowKey, subRows)
                ] }, `row-group-${rowKey}`);
              }),
              hasColumnTotals && /* @__PURE__ */ jsxs5("tr", { className: tableStyles2.stickyLastRow, children: [
                /* @__PURE__ */ jsx12(
                  "th",
                  {
                    scope: "row",
                    className: clsx4(tableStyles2.stickyFirstColumn, tableStyles2.boltCell),
                    title: totalLabel,
                    style: getTableCellWidthStyle(firstColumnWidth),
                    children: totalLabel
                  }
                ),
                columnValues.flatMap(
                  (columnValue) => measures.map((measure, idx) => {
                    const show = columnTotalsSet.has(String(measure.key));
                    const mi = measureIndexByKey.get(String(measure.key)) ?? -1;
                    const key = `col-total-${String(columnValue)}-${measure.key}-${idx}`;
                    const value = getMeasureTotal(
                      colTotals,
                      String(columnValue),
                      mi,
                      measures.length
                    );
                    let displayValue = value;
                    if (measure.showAsPercentage) {
                      displayValue = getPercentageDisplay(
                        100,
                        measure.percentageDecimalPlaces ?? 0
                      );
                    } else if (measure.accessor) {
                      displayValue = measure.accessor({ [measure.key]: value });
                    }
                    const columnValueDisplay = show ? displayValue : "";
                    return /* @__PURE__ */ jsx12("td", { className: tableStyles2.boltCell, title: columnValueDisplay, children: columnValueDisplay }, key);
                  })
                ),
                hasRowTotals && measures.filter((measure) => rowTotalsSet.has(measure.key)).map((measure, idx) => {
                  const measureIndex = measureIndexByKey.get(measure.key) ?? -1;
                  const key = `grand-total-${measure.key}-${idx}`;
                  const value = grandTotals[measureIndex] ?? 0;
                  let displayValue = value;
                  if (measure.showAsPercentage) {
                    displayValue = getPercentageDisplay(
                      100,
                      measure.percentageDecimalPlaces ?? 0
                    );
                  } else if (measure.accessor) {
                    displayValue = measure.accessor({ [measure.key]: value });
                  }
                  return /* @__PURE__ */ jsx12("td", { className: tableStyles2.boltCell, title: displayValue, children: displayValue }, key);
                })
              ] }, "totals-row")
            ] })
          ]
        }
      )
    }
  ) });
};

// src/components/charts/tables/Table/TablePaginated/TablePaginated.tsx
import * as React from "react";
import styles14 from "./tables.module-6REGLCSQ.module.css";
import clsx8 from "clsx";

// src/components/charts/tables/Table/components/TableHeader/TableHeader.tsx
import { IconCaretDownFilled, IconCaretUpDownFilled, IconCaretUpFilled } from "@tabler/icons-react";
import styles11 from "./TableHeader.module-MSGDXZU6.module.css";

// src/components/charts/tables/Table/table.types.ts
var TableSortDirection = {
  ASC: "asc",
  DESC: "desc"
};
var TableHeaderAlign = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right"
};

// src/components/charts/tables/Table/components/TableHeader/TableHeader.tsx
import tableStyles3 from "./tables.module-6REGLCSQ.module.css";
import clsx5 from "clsx";
import { useEffect as useEffect3, useState as useState4 } from "react";

// src/hooks/useDebounce.hook.ts
import { useRef as useRef8, useEffect as useEffect2, useCallback as useCallback2 } from "react";
function useDebounce(fn, delay = 300) {
  const timerRef = useRef8(null);
  const fnRef = useRef8(fn);
  useEffect2(() => {
    fnRef.current = fn;
  }, [fn]);
  const debouncedFn = useCallback2(
    (...args) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        fnRef.current(...args);
      }, delay);
    },
    [delay]
  );
  useEffect2(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return debouncedFn;
}

// src/components/charts/tables/Table/components/TableHeader/TableHeader.tsx
import { jsx as jsx13, jsxs as jsxs6 } from "react/jsx-runtime";
var getHeaderAriaSort = (sort, header) => {
  return sort?.id === header.id ? sort.direction === "asc" ? "ascending" : "descending" : "none";
};
var getHeaderAriaLabel = (sort, header) => {
  return sort?.id === header.id ? sort.direction === "asc" ? `Sort by ${header.title}, currently ascending. Activate to sort descending.` : `Sort by ${header.title}, currently descending. Activate to sort ascending.` : `Sort by ${header.title}. Activate to sort ascending.`;
};
var TableHeader = ({
  headers,
  sort,
  showIndex,
  onSortChange
}) => {
  const [currentSort, setCurrentSort] = useState4(sort);
  const debouncedUpdateState = useDebounce((value) => {
    if (value === void 0 && value === sort) {
      return;
    }
    if (value?.id === sort?.id && value?.direction === sort?.direction) {
      return;
    }
    onSortChange?.(value);
  });
  useEffect3(() => {
    debouncedUpdateState(currentSort);
  }, [currentSort, debouncedUpdateState]);
  const getSortIcon = (header) => {
    if (!currentSort) return /* @__PURE__ */ jsx13(IconCaretUpDownFilled, {});
    if (currentSort.id === header.id) {
      if (currentSort.direction === TableSortDirection.ASC) {
        return /* @__PURE__ */ jsx13(IconCaretUpFilled, {});
      } else if (currentSort.direction === TableSortDirection.DESC) {
        return /* @__PURE__ */ jsx13(IconCaretDownFilled, {});
      }
    }
    return /* @__PURE__ */ jsx13(IconCaretUpDownFilled, {});
  };
  const handleNewSortClick = (clickedHeader) => {
    if (!clickedHeader) return setCurrentSort(void 0);
    if (currentSort?.id !== clickedHeader)
      return setCurrentSort({ id: clickedHeader, direction: TableSortDirection.ASC });
    if (currentSort?.direction === void 0)
      return setCurrentSort({ id: clickedHeader, direction: TableSortDirection.ASC });
    if (currentSort?.direction === TableSortDirection.ASC)
      return setCurrentSort({ id: clickedHeader, direction: TableSortDirection.DESC });
    return setCurrentSort(void 0);
  };
  return /* @__PURE__ */ jsx13("thead", { className: styles11.tableHeader, children: /* @__PURE__ */ jsxs6("tr", { children: [
    showIndex && /* @__PURE__ */ jsx13("th", { className: clsx5(tableStyles3.mutedCell, tableStyles3.stickyFirstColumn), children: "#" }),
    headers.map((header) => /* @__PURE__ */ jsx13(
      "th",
      {
        className: styles11.tableHeaderCell,
        style: { minWidth: header.minWidth },
        scope: "col",
        "aria-sort": getHeaderAriaSort(sort, header),
        children: /* @__PURE__ */ jsxs6(
          "button",
          {
            onClick: () => handleNewSortClick(header.id),
            "aria-label": getHeaderAriaLabel(sort, header),
            children: [
              header.title,
              getSortIcon(header)
            ]
          }
        )
      },
      header.id
    ))
  ] }) });
};

// src/components/charts/tables/Table/components/TableBody/TableBody.tsx
import tableBodyStyles from "./TableBody.module-VF3UEHUT.module.css";
import clsx7 from "clsx";

// src/components/shared/ActionIcon/ActionIcon.tsx
import clsx6 from "clsx";
import styles12 from "./ActionIcon.module-NQH2HPCG.module.css";
import { jsx as jsx14 } from "react/jsx-runtime";
var ActionIcon = ({ icon: Icon, className, ...props }) => {
  return /* @__PURE__ */ jsx14("button", { className: clsx6(styles12.actionIcon, className), ...props, children: /* @__PURE__ */ jsx14(Icon, { className: styles12.icon }) });
};

// src/components/charts/tables/Table/components/TableBody/TableBody.tsx
import { IconCopy, IconCopyCheckFilled } from "@tabler/icons-react";
import { useState as useState5 } from "react";
import tableStyles4 from "./tables.module-6REGLCSQ.module.css";
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var TableBody = ({
  headers,
  rows,
  pageSize,
  page,
  showIndex,
  onRowIndexClick,
  bottomRef,
  isLoading,
  hasMoreData
}) => {
  const hasPaginatedIndex = pageSize !== void 0 && page !== void 0;
  const showBottomRef = !isLoading && hasMoreData && bottomRef !== void 0;
  return /* @__PURE__ */ jsxs7("tbody", { className: tableBodyStyles.tableBody, children: [
    rows.map((row, rowIndex) => /* @__PURE__ */ jsxs7(
      "tr",
      {
        onClick: () => onRowIndexClick?.(rowIndex),
        className: clsx7(rowIndex === rows.length - 1 && tableStyles4.tableLastRow),
        children: [
          showIndex && /* @__PURE__ */ jsx15("td", { className: clsx7(tableStyles4.mutedCell, tableStyles4.stickyFirstColumn), children: hasPaginatedIndex ? pageSize * page + rowIndex + 1 : rowIndex + 1 }),
          headers.map((header, cellIndex) => /* @__PURE__ */ jsx15(
            TableBodyCell,
            {
              header,
              row,
              rowIndex,
              cellIndex
            },
            cellIndex
          ))
        ]
      },
      rowIndex
    )),
    showBottomRef && /* @__PURE__ */ jsx15("tr", { children: /* @__PURE__ */ jsx15("td", { colSpan: (showIndex ? 1 : 0) + headers.length, className: tableStyles4.bottomRefCell, children: /* @__PURE__ */ jsx15("div", { ref: bottomRef, style: { height: 1 } }) }) })
  ] });
};
var TableBodyCell = ({ header, row }) => {
  const rawValue = header.id !== void 0 ? (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    row[header.id]
  ) : void 0;
  const value = header.accessor !== void 0 ? header.accessor(row) : rawValue;
  if (header.cell) {
    return header.cell({ value, className: tableBodyStyles.tableBodyCell });
  }
  return /* @__PURE__ */ jsx15(TableBodyCellWithCopy, { align: header.align, value, style: header.cellStyle?.(rawValue), children: value });
};
var TableBodyCellWithCopy = ({
  value,
  align = "left",
  children,
  style: style2
}) => {
  const [isPressedCopy, setIsPressedCopy] = useState5(false);
  const handleCopy = () => {
    setIsPressedCopy(true);
    if (value !== void 0) {
      navigator.clipboard.writeText(String(value));
    }
  };
  return /* @__PURE__ */ jsxs7(
    "td",
    {
      title: value,
      style: { textAlign: align, ...style2 },
      onMouseLeave: () => setIsPressedCopy(false),
      children: [
        /* @__PURE__ */ jsx15(
          ActionIcon,
          {
            title: `Copy: ${String(value)}`,
            onMouseDown: handleCopy,
            icon: isPressedCopy ? IconCopyCheckFilled : IconCopy,
            className: clsx7(
              tableBodyStyles.copyButton,
              align === TableHeaderAlign.RIGHT && tableBodyStyles.leftAlign
            ),
            onClick: handleCopy
          }
        ),
        children
      ]
    }
  );
};

// src/components/charts/tables/Table/components/TablePagination/TablePagination.tsx
import {
  IconChevronLeft,
  IconChevronRight as IconChevronRight2,
  IconChevronsLeft,
  IconChevronsRight
} from "@tabler/icons-react";
import styles13 from "./TablePagination.module-VGIQ7VN7.module.css";
import { useEffect as useEffect4 } from "react";
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var getTableTotalPages = (total, pageSize) => {
  return total ? Math.ceil(total / pageSize) : void 0;
};
var TablePagination = ({
  page,
  pageSize,
  paginationLabel,
  total,
  onPageChange
}) => {
  const totalPages = getTableTotalPages(total, pageSize);
  const disabledPrev = page <= 0;
  const disabledNext = !totalPages || page >= totalPages - 1;
  useEffect4(() => {
    if (totalPages && page >= totalPages) {
      onPageChange(0);
    }
  }, [totalPages, page, onPageChange]);
  return /* @__PURE__ */ jsx16("div", { className: styles13.tablePagination, "aria-label": "Table pagination controls", children: /* @__PURE__ */ jsxs8("div", { className: styles13.tablePaginationCentral, children: [
    /* @__PURE__ */ jsxs8("div", { className: styles13.tablePaginationCentralButtons, children: [
      /* @__PURE__ */ jsx16(
        ActionIcon,
        {
          icon: IconChevronsLeft,
          onClick: () => {
            onPageChange(0);
          },
          disabled: disabledPrev,
          "aria-label": "First page"
        }
      ),
      /* @__PURE__ */ jsx16(
        ActionIcon,
        {
          icon: IconChevronLeft,
          onClick: () => {
            onPageChange(page - 1);
          },
          disabled: disabledPrev,
          "aria-label": "Previous page"
        }
      )
    ] }),
    /* @__PURE__ */ jsx16("span", { children: paginationLabel ?? `Page ${page + 1} of ${totalPages ?? "?"}` }),
    /* @__PURE__ */ jsxs8("div", { className: styles13.tablePaginationCentralButtons, children: [
      /* @__PURE__ */ jsx16(
        ActionIcon,
        {
          icon: IconChevronRight2,
          onClick: () => {
            onPageChange(page + 1);
          },
          disabled: disabledNext,
          "aria-label": "Next page"
        }
      ),
      /* @__PURE__ */ jsx16(
        ActionIcon,
        {
          icon: IconChevronsRight,
          onClick: () => totalPages && onPageChange(totalPages - 1),
          disabled: disabledNext,
          "aria-label": "Last page"
        }
      )
    ] })
  ] }) });
};

// src/components/charts/tables/Table/TablePaginated/TablePaginated.tsx
import { jsx as jsx17, jsxs as jsxs9 } from "react/jsx-runtime";
var TablePaginated = React.forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props, ref) => {
    const {
      headers,
      rows,
      className,
      total,
      page,
      pageSize,
      paginationLabel,
      sort,
      showIndex = false,
      onRowIndexClick,
      onPageChange,
      onSortChange
    } = props;
    return /* @__PURE__ */ jsxs9("div", { ref, className: clsx8(styles14.tableContainer, className), children: [
      /* @__PURE__ */ jsx17("div", { className: styles14.tableContainerScroll, children: /* @__PURE__ */ jsxs9("table", { className: styles14.table, children: [
        /* @__PURE__ */ jsx17(
          TableHeader,
          {
            showIndex,
            headers,
            sort,
            onSortChange
          }
        ),
        /* @__PURE__ */ jsx17(
          TableBody,
          {
            onRowIndexClick,
            showIndex,
            headers,
            rows,
            page,
            pageSize
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx17(
        TablePagination,
        {
          page,
          pageSize,
          paginationLabel,
          total,
          onPageChange
        }
      )
    ] });
  }
);
TablePaginated.displayName = "TablePaginated";

// src/components/charts/tables/Table/TablePaginated/TablePaginated.hooks.ts
import { useMemo as useMemo4 } from "react";
var useTableGetRowsPerPage = ({
  availableHeight,
  headerHeight,
  rowHeight,
  footerHeight = 0
}) => useMemo4(() => {
  const h = availableHeight;
  if (!h) return 0;
  let available = h - headerHeight - footerHeight;
  if (available < 0) available = 0;
  const rows = Math.floor(available / rowHeight);
  return Math.max(0, rows);
}, [availableHeight, headerHeight, rowHeight, footerHeight]);

// src/components/charts/tables/Table/TableScrollable/TableScrollable.tsx
import clsx9 from "clsx";
import tableStyles5 from "./tables.module-6REGLCSQ.module.css";

// src/components/charts/tables/Table/TableScrollable/TableScrollable.hooks.ts
import { useEffect as useEffect5, useRef as useRef9 } from "react";
function useInfiniteScroll({
  rootRef,
  enabled = true,
  onPrefetch,
  bottomDistanceToPrefetch = 750
}) {
  const loadingRef = useRef9(false);
  useEffect5(() => {
    if (!enabled) return;
    const el = rootRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!enabled) return;
      if (loadingRef.current) return;
      const { scrollTop, clientHeight, scrollHeight } = el;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      if (distanceFromBottom <= bottomDistanceToPrefetch) {
        loadingRef.current = true;
        onPrefetch();
      }
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [enabled, onPrefetch, rootRef, bottomDistanceToPrefetch]);
  useEffect5(() => {
    if (enabled) loadingRef.current = false;
  }, [enabled]);
}

// src/components/charts/tables/Table/TableScrollable/TableScrollable.tsx
import { forwardRef as forwardRef2, useImperativeHandle, useRef as useRef10 } from "react";
import { jsx as jsx18, jsxs as jsxs10 } from "react/jsx-runtime";
var TableScrollable = forwardRef2(
  ({
    sort,
    onSortChange,
    showIndex,
    headers,
    rows,
    onNextPage,
    hasMoreData = true,
    className,
    onRowIndexClick,
    isLoading = false,
    loadingLabel = "Loading...",
    bottomDistanceToPrefetch
  }, ref) => {
    const scrollRef = useRef10(null);
    const sentinelRef = useRef10(null);
    useImperativeHandle(
      ref,
      () => ({
        scrollToTop: (behavior = "auto") => {
          scrollRef.current?.scrollTo({ top: 0, behavior });
        },
        getScrollElement: () => scrollRef.current
      }),
      []
    );
    useInfiniteScroll({
      rootRef: scrollRef,
      enabled: hasMoreData && !isLoading,
      onPrefetch: onNextPage,
      bottomDistanceToPrefetch
    });
    return /* @__PURE__ */ jsx18("div", { className: clsx9(tableStyles5.tableFullContainer, className), children: /* @__PURE__ */ jsxs10(
      "div",
      {
        ref: scrollRef,
        className: clsx9(tableStyles5.tableAdjustedContainer, tableStyles5.fullWidth),
        children: [
          /* @__PURE__ */ jsxs10(
            "table",
            {
              className: clsx9(tableStyles5.table, tableStyles5.cellWrap),
              "aria-label": "Scrollable table",
              children: [
                /* @__PURE__ */ jsx18(
                  TableHeader,
                  {
                    showIndex,
                    headers,
                    sort,
                    onSortChange
                  }
                ),
                /* @__PURE__ */ jsx18(
                  TableBody,
                  {
                    showIndex,
                    headers,
                    rows,
                    onRowIndexClick,
                    bottomRef: sentinelRef,
                    isLoading,
                    hasMoreData
                  }
                )
              ]
            }
          ),
          isLoading && /* @__PURE__ */ jsx18("div", { className: tableStyles5.loadingIndicator, children: loadingLabel })
        ]
      }
    ) });
  }
);
TableScrollable.displayName = "TableScrollable";

// src/components/charts/shared/ChartTabs/ChartTabs.tsx
import { IconChevronLeft as IconChevronLeft2, IconChevronRight as IconChevronRight3 } from "@tabler/icons-react";

// src/components/charts/shared/ChartTabs/ChartTabsItem/ChartTabsItem.tsx
import styles15 from "./ChartTabs.module-Q52WMQUT.module.css";
import clsx10 from "clsx";
import { jsx as jsx19, jsxs as jsxs11 } from "react/jsx-runtime";
var ChartTabsItem = ({
  id,
  active,
  label,
  value,
  slot,
  className,
  onClick
}) => {
  return /* @__PURE__ */ jsxs11(
    "button",
    {
      id: `chart-kpi-tab-${id}`,
      className: clsx10(styles15.tab, active && styles15.active, className),
      type: "button",
      role: "tab",
      "aria-selected": active,
      onClick,
      children: [
        /* @__PURE__ */ jsx19("span", { className: styles15.label, children: label }),
        value && /* @__PURE__ */ jsx19("span", { className: styles15.value, children: value }),
        slot
      ]
    }
  );
};

// src/components/charts/shared/ChartTabs/ChartTabs.tsx
import styles16 from "./ChartTabs.module-Q52WMQUT.module.css";

// src/hooks/useHorizontalScroll.hooks.ts
import { useCallback as useCallback3, useEffect as useEffect6, useRef as useRef11, useState as useState6 } from "react";
var DEFAULT_SCROLL_STEP = 200;
var useHorizontalScroll = (scrollStep = DEFAULT_SCROLL_STEP) => {
  const scrollRef = useRef11(null);
  const [canScrollRight, setCanScrollRight] = useState6(false);
  const [canScrollLeft, setCanScrollLeft] = useState6(false);
  const updateScrollState = useCallback3(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  const handleScrollRight = () => {
    scrollRef.current?.scrollBy({ left: scrollStep, behavior: "smooth" });
  };
  const handleScrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -scrollStep, behavior: "smooth" });
  };
  useEffect6(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    el.addEventListener("scroll", updateScrollState);
    updateScrollState();
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", updateScrollState);
    };
  }, [updateScrollState]);
  return { scrollRef, canScrollLeft, canScrollRight, handleScrollLeft, handleScrollRight };
};

// src/components/charts/shared/ChartTabs/ChartTabs.tsx
import clsx11 from "clsx";
import { jsx as jsx20, jsxs as jsxs12 } from "react/jsx-runtime";
var ChartTabs = ({ value, items, className, onChange }) => {
  const { scrollRef, canScrollLeft, canScrollRight, handleScrollLeft, handleScrollRight } = useHorizontalScroll();
  return /* @__PURE__ */ jsxs12("div", { className: clsx11(styles16.tabs, className), children: [
    canScrollLeft && /* @__PURE__ */ jsx20(ActionIcon, { icon: IconChevronLeft2, onClick: handleScrollLeft }),
    /* @__PURE__ */ jsx20("div", { role: "tablist", className: styles16.scroll, ref: scrollRef, children: items.map((item) => /* @__PURE__ */ jsx20(
      ChartTabsItem,
      {
        ...item,
        active: item.id === value,
        onClick: () => onChange(item.id)
      },
      item.id
    )) }),
    canScrollRight && /* @__PURE__ */ jsx20(ActionIcon, { icon: IconChevronRight3, onClick: handleScrollRight })
  ] });
};

// src/components/editors/dates/DateRangePicker/DateRangePicker.tsx
import { useState as useState7 } from "react";
import { DayPicker } from "react-day-picker";
import "./DateRangePicker-4W3QHK4R.css";

// src/components/editors/dates/DateRangePicker/DateRangePickerChevron.tsx
import { IconCaretLeftFilled } from "@tabler/icons-react";
import styles17 from "./DateRangePickerChevron.module-U3RB6ZHO.module.css";
import clsx12 from "clsx";
import { jsx as jsx21 } from "react/jsx-runtime";
var SMALL_SIZE = 18;
var DateRangePickerChevron = ({
  orientation,
  size
}) => {
  const rotation = orientation === "left" ? void 0 : "rotate(180deg)";
  const isSmallChevron = size === SMALL_SIZE;
  return /* @__PURE__ */ jsx21(
    "span",
    {
      "data-testid": "date-range-picker-chevron",
      className: clsx12(styles17.chevron, isSmallChevron && styles17.small),
      style: {
        transform: rotation
      },
      children: /* @__PURE__ */ jsx21(IconCaretLeftFilled, {})
    }
  );
};

// src/utils/date.utils.ts
var isSameDate = (a, b) => a?.getTime() === b?.getTime();
var isSameDateRange = (a, b) => isSameDate(a?.from, b?.from) && isSameDate(a?.to, b?.to);
var endOfDayUTC = (date) => {
  const d = new Date(date);
  d.setUTCHours(23, 59, 59, 999);
  return d;
};

// src/components/editors/dates/DateRangePicker/DateRangePicker.tsx
import * as rdpLocales from "react-day-picker/locale";
import { jsx as jsx22 } from "react/jsx-runtime";
var DateRangePicker = ({
  value,
  numberOfMonths = 1,
  locale = "en",
  onChange
}) => {
  const [month, setMonth] = useState7(value?.from ?? /* @__PURE__ */ new Date());
  const handleChange = (range) => {
    if (range?.to) {
      range.to = endOfDayUTC(range.to);
    }
    onChange(range);
  };
  const now = /* @__PURE__ */ new Date();
  const endMonth = new Date(now.getFullYear() + 2, now.getMonth());
  return /* @__PURE__ */ jsx22(
    DayPicker,
    {
      month,
      onMonthChange: setMonth,
      numberOfMonths,
      components: { Chevron: DateRangePickerChevron },
      locale: rdpLocales[locale],
      mode: "range",
      navLayout: "around",
      onSelect: handleChange,
      selected: value,
      timeZone: "UTC",
      showOutsideDays: true,
      captionLayout: "dropdown-years",
      endMonth,
      animate: true
    }
  );
};

// src/components/editors/dates/DateRangePickerField/DateRangePickerField.tsx
import { useState as useState8 } from "react";

// src/components/shared/Field/FieldHeader.tsx
import clsx13 from "clsx";
import styles18 from "./FieldHeader.module-QOLBH472.module.css";
import { jsx as jsx23, jsxs as jsxs13 } from "react/jsx-runtime";
var FieldHeader = ({
  label,
  required,
  requiredLabel = "Required",
  className
}) => {
  if (!label && !required) {
    return null;
  }
  return /* @__PURE__ */ jsxs13("label", { className: clsx13(styles18.fieldHeader, className), children: [
    label,
    required && /* @__PURE__ */ jsx23("span", { children: requiredLabel })
  ] });
};

// src/components/shared/Dropdown/Dropdown.tsx
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { jsx as jsx24, jsxs as jsxs14 } from "react/jsx-runtime";
var Dropdown = ({
  triggerComponent,
  children,
  disabled,
  open,
  side = "bottom",
  align = "start",
  avoidCollisions = true,
  onOpenChange
}) => {
  return /* @__PURE__ */ jsxs14(DropdownMenu.Root, { open, onOpenChange, modal: false, children: [
    /* @__PURE__ */ jsx24(DropdownMenu.Trigger, { asChild: true, disabled, children: triggerComponent }),
    /* @__PURE__ */ jsx24(
      DropdownMenu.Content,
      {
        side,
        align,
        style: { zIndex: 5 },
        avoidCollisions,
        children
      }
    )
  ] });
};

// src/components/editors/selects/shared/SelectFieldTrigger/SelectFieldTrigger.tsx
import styles20 from "./SelectFieldTrigger.module-M6BRE7IY.module.css";
import { IconCaretDownFilled as IconCaretDownFilled2, IconLoader2 as IconLoader22, IconX } from "@tabler/icons-react";
import clsx15 from "clsx";
import { forwardRef as forwardRef3 } from "react";

// src/components/shared/GhostButton/GhostButton.tsx
import React3 from "react";
import clsx14 from "clsx";
import styles19 from "./GhostButton.module-33OGL7WW.module.css";
import { jsx as jsx25, jsxs as jsxs15 } from "react/jsx-runtime";
var GhostButton = React3.forwardRef(
  ({ startIcon: StartIcon, endIcon: EndIcon, children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs15("button", { ref, className: clsx14(styles19.ghostButton, className), ...props, children: [
      StartIcon && /* @__PURE__ */ jsx25(StartIcon, {}),
      /* @__PURE__ */ jsx25("span", { children }),
      EndIcon && /* @__PURE__ */ jsx25(EndIcon, {})
    ] });
  }
);
GhostButton.displayName = "GhostButton";

// src/components/editors/selects/shared/SelectFieldTrigger/SelectFieldTrigger.tsx
import { jsx as jsx26, jsxs as jsxs16 } from "react/jsx-runtime";
var SelectFieldTrigger = forwardRef3(
  ({
    startIcon: StartIcon,
    valueLabel,
    placeholder = "Select",
    isClearable,
    isLoading,
    onClear,
    error = false,
    variant = "default",
    ...props
  }, ref) => {
    const displayValue = valueLabel || placeholder;
    const showClearButton = valueLabel && isClearable;
    const handleClear = (e) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
    };
    if (variant === "ghost") {
      return /* @__PURE__ */ jsxs16(GhostButton, { ref, endIcon: isLoading ? IconLoader22 : IconCaretDownFilled2, ...props, children: [
        displayValue,
        showClearButton && /* @__PURE__ */ jsx26(IconX, { onPointerDown: handleClear })
      ] });
    }
    return /* @__PURE__ */ jsxs16(
      "button",
      {
        ref,
        className: clsx15(
          styles20.selectFieldTrigger,
          valueLabel && styles20.filled,
          error && styles20.error
        ),
        ...props,
        children: [
          StartIcon && /* @__PURE__ */ jsx26(StartIcon, {}),
          /* @__PURE__ */ jsx26("span", { children: displayValue }),
          showClearButton && /* @__PURE__ */ jsx26(IconX, { onPointerDown: handleClear }),
          isLoading ? /* @__PURE__ */ jsx26(IconLoader22, { className: styles20.loading }) : /* @__PURE__ */ jsx26(IconCaretDownFilled2, {})
        ]
      }
    );
  }
);
SelectFieldTrigger.displayName = "SelectFieldTrigger";

// src/components/editors/selects/shared/SelectFieldContent/SelectFieldContent.tsx
import { useEffect as useEffect7, useRef as useRef12 } from "react";
import styles21 from "./SelectFieldContent.module-GZYJOPUB.module.css";
import clsx16 from "clsx";
import { jsx as jsx27 } from "react/jsx-runtime";
var SelectFieldContent = ({
  children,
  autoFocus,
  className,
  fitContent = false,
  ...props
}) => {
  const containerRef = useRef12(null);
  const currentIndex = useRef12(0);
  useEffect7(() => {
    const container = containerRef.current;
    if (!container) return;
    const items = Array.from(
      container.querySelectorAll('[role="menuitem"], [role="searchbox"], button')
    );
    const focusItem = (index) => {
      if (index >= 0 && index < items.length) {
        items[index]?.focus();
        currentIndex.current = index;
      }
    };
    const onKeyDown = (e) => {
      if (items.length === 0) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          focusItem((currentIndex.current + 1) % items.length);
          break;
        case "ArrowUp":
          e.preventDefault();
          focusItem((currentIndex.current - 1 + items.length) % items.length);
          break;
        case "Home":
          e.preventDefault();
          focusItem(0);
          break;
        case "End":
          e.preventDefault();
          focusItem(items.length - 1);
          break;
      }
    };
    container.addEventListener("keydown", onKeyDown);
    if (autoFocus) {
      setTimeout(() => {
        currentIndex.current = 0;
        focusItem(0);
      }, 0);
    }
    return () => {
      container.removeEventListener("keydown", onKeyDown);
    };
  }, [autoFocus, children]);
  return /* @__PURE__ */ jsx27(
    "div",
    {
      ref: containerRef,
      tabIndex: -1,
      className: clsx16(styles21.selectFieldContent, fitContent && styles21.fitContent, className),
      ...props,
      children
    }
  );
};
var SelectFieldContentList = ({ children, disabled }) => {
  return /* @__PURE__ */ jsx27("div", { className: clsx16(styles21.selectFieldContentList, disabled && styles21.disabled), children });
};

// src/components/shared/Field/FieldFeedback.tsx
import clsx17 from "clsx";
import styles22 from "./FieldFeedback.module-F7Z33KCS.module.css";
import { jsx as jsx28 } from "react/jsx-runtime";
var FieldFeedback = ({ message, variant, className }) => {
  return /* @__PURE__ */ jsx28("p", { className: clsx17(styles22.fieldFeedback, variant && styles22[variant], className), children: message });
};

// src/components/shared/Button/Button.tsx
import styles23 from "./Button.module-LD3OBJVA.module.css";
import clsx18 from "clsx";
import { jsx as jsx29, jsxs as jsxs17 } from "react/jsx-runtime";
var Button = ({
  children,
  variant = "primary",
  size = "medium",
  startIcon: StartIcon,
  endIcon: EndIcon,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsxs17("button", { className: clsx18(styles23.button, styles23[variant], styles23[size], className), ...props, children: [
    StartIcon && /* @__PURE__ */ jsx29(StartIcon, {}),
    /* @__PURE__ */ jsx29("span", { children }),
    EndIcon && /* @__PURE__ */ jsx29(EndIcon, {})
  ] });
};

// src/components/editors/dates/DateRangePickerField/DateRangePickerField.tsx
import styles24 from "./DateRangePickerField.module-CURG3KIS.module.css";
import { jsx as jsx30, jsxs as jsxs18 } from "react/jsx-runtime";
var getDateRangePickerLabel = (dateRange, displayValue) => {
  if (!dateRange) return void 0;
  if (displayValue || displayValue === "") return displayValue;
  const { from, to } = dateRange;
  if (!from || !to) return void 0;
  return `${from ? from.toLocaleDateString() : ""} - ${to ? to.toLocaleDateString() : ""}`;
};
var DateRangePickerField = ({
  locale,
  displayValue,
  value,
  startIcon,
  label,
  disabled,
  placeholder,
  required,
  error,
  errorMessage,
  clearable,
  numberOfMonths = 1,
  submitLabel = "Apply",
  avoidCollisions,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState8(false);
  const [currentDateRange, setCurrentDateRange] = useState8(value);
  const valueLabel = getDateRangePickerLabel(value, displayValue);
  const hasError = error || !!errorMessage;
  const handleChange = () => {
    onChange(currentDateRange);
    setIsOpen(false);
  };
  const handleClear = () => {
    setCurrentDateRange(void 0);
    onChange(void 0);
  };
  const handleOpenChange = (open) => {
    if (open) {
      setIsOpen(true);
      return;
    }
    setCurrentDateRange(value);
    setIsOpen(false);
  };
  const isSubmitDisabled = isSameDateRange(currentDateRange, value);
  return /* @__PURE__ */ jsxs18("div", { children: [
    /* @__PURE__ */ jsx30(FieldHeader, { label, required }),
    /* @__PURE__ */ jsx30(
      Dropdown,
      {
        open: isOpen,
        onOpenChange: handleOpenChange,
        disabled,
        avoidCollisions,
        triggerComponent: /* @__PURE__ */ jsx30(
          SelectFieldTrigger,
          {
            startIcon,
            "aria-label": "Select options",
            placeholder,
            disabled,
            valueLabel,
            onClear: handleClear,
            isClearable: clearable,
            error: hasError
          }
        ),
        children: /* @__PURE__ */ jsxs18(SelectFieldContent, { fitContent: true, children: [
          /* @__PURE__ */ jsx30(
            DateRangePicker,
            {
              locale,
              numberOfMonths,
              value: currentDateRange,
              onChange: setCurrentDateRange
            }
          ),
          /* @__PURE__ */ jsx30(
            Button,
            {
              size: "small",
              className: styles24.submitButton,
              onClick: handleChange,
              disabled: isSubmitDisabled,
              children: submitLabel
            }
          )
        ] })
      }
    ),
    errorMessage && /* @__PURE__ */ jsx30(FieldFeedback, { message: errorMessage, variant: "error" })
  ] });
};

// src/components/editors/inputs/NumberField/NumberField.tsx
import { forwardRef as forwardRef5 } from "react";

// src/components/editors/inputs/shared/InputField/InputField.tsx
import { IconX as IconX2 } from "@tabler/icons-react";
import styles25 from "./InputField.module-JKLU4D4O.module.css";
import clsx19 from "clsx";
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx31, jsxs as jsxs19 } from "react/jsx-runtime";
var InputField = forwardRef4(
  ({
    label,
    required,
    value = "",
    disabled,
    placeholder = "Enter text",
    role,
    startIcon: StartIcon,
    endIcon: EndIcon,
    onChange,
    className,
    clearable = false,
    type = "text",
    error = false,
    errorMessage,
    requiredLabel,
    ...props
  }, ref) => {
    const showClearButton = value && clearable;
    const hasError = error || errorMessage;
    return /* @__PURE__ */ jsxs19("div", { className, children: [
      /* @__PURE__ */ jsx31(FieldHeader, { label, required, requiredLabel }),
      /* @__PURE__ */ jsxs19("div", { className: clsx19(styles25.input, hasError && styles25.error), children: [
        StartIcon && /* @__PURE__ */ jsx31(StartIcon, {}),
        /* @__PURE__ */ jsx31(
          "input",
          {
            ref,
            type,
            role,
            value,
            disabled,
            placeholder,
            onChange: (e) => onChange(e.target.value),
            required,
            ...props
          }
        ),
        /* @__PURE__ */ jsx31(
          IconX2,
          {
            className: clsx19(styles25.clearIcon, showClearButton && styles25.clearIconVisible),
            onClick: () => onChange("")
          }
        ),
        EndIcon && /* @__PURE__ */ jsx31(EndIcon, {})
      ] }),
      errorMessage && /* @__PURE__ */ jsx31(FieldFeedback, { variant: "error", message: errorMessage })
    ] });
  }
);
InputField.displayName = "InputField";

// src/components/editors/inputs/NumberField/NumberField.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var NumberField = forwardRef5(
  ({
    value = null,
    placeholder = "Enter number",
    onChange,
    error,
    errorMessage,
    clearable,
    ...props
  }, ref) => {
    const handleChange = (value2) => {
      if (value2 === "") {
        onChange?.(null);
        return;
      }
      const numericValue = Number(value2);
      if (!isNaN(numericValue) && isFinite(numericValue)) {
        onChange?.(numericValue);
      }
    };
    return /* @__PURE__ */ jsx32(
      InputField,
      {
        ...props,
        value: value?.toString() || "",
        placeholder,
        onChange: handleChange,
        type: "number",
        clearable,
        error,
        errorMessage,
        ref
      }
    );
  }
);
NumberField.displayName = "NumberField";

// src/components/editors/inputs/TextField/TextField.tsx
import { forwardRef as forwardRef6 } from "react";
import { Fragment as Fragment5, jsx as jsx33, jsxs as jsxs20 } from "react/jsx-runtime";
var TextField = forwardRef6(
  ({ value = "", placeholder = "Enter text", maxLength, error, errorMessage, ...props }, ref) => {
    return /* @__PURE__ */ jsxs20(Fragment5, { children: [
      /* @__PURE__ */ jsx33(
        InputField,
        {
          value,
          placeholder,
          ref,
          maxLength,
          clearable: true,
          error,
          errorMessage,
          ...props
        }
      ),
      maxLength && /* @__PURE__ */ jsx33(FieldFeedback, { message: `${value?.length}/${maxLength} Characters` })
    ] });
  }
);
TextField.displayName = "TextField";

// src/components/editors/selects/MultiSelectField/MultiSelectField.tsx
import { Fragment as Fragment6, useEffect as useEffect9, useMemo as useMemo5, useRef as useRef13, useState as useState9 } from "react";

// src/components/editors/selects/shared/SelectFieldContent/SelectFieldOptions/SelectFieldOption/SelectFieldOption.tsx
import * as DropdownMenu2 from "@radix-ui/react-dropdown-menu";
import styles26 from "./SelectFieldOption.module-ZXJXUZWN.module.css";
import clsx20 from "clsx";
import { jsx as jsx34, jsxs as jsxs21 } from "react/jsx-runtime";
var SelectListOption = ({
  value,
  isSelected,
  label,
  rightLabel,
  startIcon,
  endIcon,
  disabled,
  ...props
}) => {
  return /* @__PURE__ */ jsxs21(
    DropdownMenu2.Item,
    {
      className: clsx20(
        styles26.selectFieldOption,
        disabled && styles26.disabled,
        isSelected && styles26.selected
      ),
      "data-value": String(value),
      ...props,
      children: [
        /* @__PURE__ */ jsxs21("span", { className: styles26.leftContent, children: [
          startIcon,
          /* @__PURE__ */ jsx34("span", { title: label, children: label })
        ] }),
        /* @__PURE__ */ jsxs21("span", { className: styles26.rightContent, children: [
          /* @__PURE__ */ jsx34("span", { title: rightLabel, children: rightLabel }),
          endIcon
        ] })
      ]
    }
  );
};

// src/components/editors/selects/shared/SelectFieldContent/SelectFieldContent.utils.ts
var groupOptionsByCategory = (options) => {
  const result = options.reduce((acc, option) => {
    if (option.category) {
      const category = option.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(option);
    }
    return acc;
  }, {});
  return Object.keys(result).length === 0 ? null : result;
};

// src/components/editors/selects/MultiSelectField/MultiSelectField.tsx
import { IconSearch, IconSquare, IconSquareCheckFilled } from "@tabler/icons-react";
import styles28 from "./selects.module-MRJADSDF.module.css";

// src/components/editors/selects/shared/useSelectSearchFocus.hook.ts
import { useEffect as useEffect8 } from "react";
var useSelectSearchFocus = (isOpen, searchFieldRef) => {
  useEffect8(() => {
    if (isOpen) {
      setTimeout(() => {
        searchFieldRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchFieldRef]);
};

// src/components/editors/selects/shared/SelectFieldContent/SelectFieldOptions/SelectFieldCategory/SelectFieldCategory.tsx
import styles27 from "./SelectFieldCategory.module-FMZMX5V2.module.css";
import { jsx as jsx35 } from "react/jsx-runtime";
var SelectFieldCategory = ({ label }) => {
  return /* @__PURE__ */ jsx35("span", { className: styles27.selectFieldCategory, children: label });
};

// src/utils/debounce.utils.ts
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

// src/components/editors/selects/MultiSelectField/MultiSelectField.tsx
import { jsx as jsx36, jsxs as jsxs22 } from "react/jsx-runtime";
function MultiSelectField({
  startIcon,
  label,
  required,
  disabled,
  disableApplyButton,
  isClearable,
  isLoading,
  isSearchable,
  noOptionsMessage,
  options,
  placeholder,
  submitLabel = "Apply",
  values = [],
  avoidCollisions,
  onChange,
  onPendingChange,
  onSearch,
  error = false,
  errorMessage,
  triggerComponent
}) {
  const [isOpen, setIsOpen] = useState9(false);
  const [searchValue, setSearchValue] = useState9("");
  const [preValues, setPreValues] = useState9(values);
  const [selectedLabel, setSelectedLabel] = useState9("");
  const searchFieldRef = useRef13(null);
  useSelectSearchFocus(isOpen, searchFieldRef);
  useEffect9(() => {
    setPreValues(values);
    onPendingChange?.(values);
  }, [JSON.stringify(values)]);
  useEffect9(() => {
    if (isLoading) {
      return;
    }
    if (!values || values.length === 0) {
      setSelectedLabel("");
      return;
    }
    const selectedOptions = values.map(
      (value) => options.find((o) => o.value === value)?.label ?? String(value)
    );
    if (selectedOptions.length > 0) {
      const newLabel = selectedOptions.join(", ");
      setSelectedLabel(`(${selectedOptions.length}) ${newLabel}`);
    }
  }, [values, options, isLoading]);
  const debouncedSearch = useMemo5(() => onSearch ? debounce(onSearch) : void 0, [onSearch]);
  const displayOptions = isSearchable && !onSearch ? options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase())) : options;
  const groupedOptions = useMemo5(() => groupOptionsByCategory(displayOptions), [displayOptions]);
  const isSubmitDisabled = preValues.every((preValue) => values.includes(preValue)) && values.every((value) => preValues.includes(value));
  const handleSelectOption = (e, newValue) => {
    e.preventDefault();
    if (newValue === void 0) return;
    if (preValues.includes(newValue)) {
      const next = preValues.filter((v) => v !== newValue);
      setPreValues(next);
      onPendingChange?.(next);
    } else {
      const next = [...preValues, newValue];
      setPreValues(next);
      onPendingChange?.(next);
    }
  };
  const handleSearch = (newSearch) => {
    setSearchValue(newSearch);
    debouncedSearch?.(newSearch);
  };
  const handleSave = (newValues) => {
    onChange(newValues);
    setIsOpen(false);
    setSearchValue("");
    onSearch?.("");
  };
  const handleClearAll = () => {
    setSearchValue("");
    onSearch?.("");
    onPendingChange?.([]);
    onChange([]);
  };
  const renderOption = (option) => {
    const { value: optionValue, category: _category, ...optionRest } = option;
    return /* @__PURE__ */ jsx36(
      SelectListOption,
      {
        value: optionValue,
        onClick: (e) => handleSelectOption(e, optionValue),
        startIcon: optionValue !== void 0 && preValues.includes(optionValue) ? /* @__PURE__ */ jsx36(IconSquareCheckFilled, {}) : /* @__PURE__ */ jsx36(IconSquare, {}),
        ...optionRest
      },
      String(optionValue)
    );
  };
  const hasError = error || !!errorMessage;
  return /* @__PURE__ */ jsxs22("div", { children: [
    /* @__PURE__ */ jsx36(FieldHeader, { label, required }),
    /* @__PURE__ */ jsx36(
      Dropdown,
      {
        open: isOpen,
        onOpenChange: setIsOpen,
        disabled,
        avoidCollisions,
        triggerComponent: triggerComponent ?? /* @__PURE__ */ jsx36(
          SelectFieldTrigger,
          {
            startIcon,
            "aria-label": "Select options",
            placeholder,
            disabled,
            valueLabel: selectedLabel,
            onClear: handleClearAll,
            isClearable,
            isLoading,
            error: hasError
          }
        ),
        children: /* @__PURE__ */ jsxs22(SelectFieldContent, { children: [
          isSearchable && /* @__PURE__ */ jsx36(
            TextField,
            {
              ref: searchFieldRef,
              startIcon: IconSearch,
              "aria-label": "Search options",
              placeholder: "Search\u2026",
              role: "searchbox",
              value: searchValue,
              onKeyDown: (e) => e.stopPropagation(),
              onChange: handleSearch,
              className: styles28.searchField
            }
          ),
          /* @__PURE__ */ jsxs22(SelectFieldContentList, { disabled: isLoading, children: [
            groupedOptions ? Object.entries(groupedOptions).map(([category, categoryOptions]) => /* @__PURE__ */ jsxs22(Fragment6, { children: [
              /* @__PURE__ */ jsx36(SelectFieldCategory, { label: category }),
              categoryOptions.map(renderOption)
            ] }, category)) : displayOptions.map(renderOption),
            noOptionsMessage && displayOptions.length === 0 && /* @__PURE__ */ jsx36(SelectListOption, { disabled: true, value: "empty", label: noOptionsMessage })
          ] }),
          /* @__PURE__ */ jsx36(
            Button,
            {
              className: styles28.submitButton,
              disabled: isSubmitDisabled || isLoading || disableApplyButton,
              variant: "primary",
              size: "medium",
              onClick: () => handleSave(preValues),
              role: "button",
              children: submitLabel
            }
          )
        ] })
      }
    ),
    errorMessage && /* @__PURE__ */ jsx36(FieldFeedback, { message: errorMessage, variant: "error" })
  ] });
}

// src/components/editors/selects/SingleSelectField/SingleSelectField.tsx
import { Fragment as Fragment7, useEffect as useEffect10, useMemo as useMemo6, useRef as useRef14, useState as useState10 } from "react";
import { IconSearch as IconSearch2 } from "@tabler/icons-react";
import styles29 from "./selects.module-MRJADSDF.module.css";
import { jsx as jsx37, jsxs as jsxs23 } from "react/jsx-runtime";
function SingleSelectField({
  label,
  required,
  value = null,
  startIcon,
  options,
  disabled,
  placeholder,
  searchable,
  clearable,
  isLoading,
  avoidCollisions,
  noOptionsMessage = "No options available",
  onChange,
  onSearch,
  error = false,
  errorMessage,
  side,
  align,
  variant,
  triggerComponent
}) {
  const [isOpen, setIsOpen] = useState10(false);
  const [searchValue, setSearchValue] = useState10("");
  const [selectedLabel, setSelectedLabel] = useState10("");
  const searchFieldRef = useRef14(null);
  useSelectSearchFocus(isOpen, searchFieldRef);
  useEffect10(() => {
    if (value === null) {
      setSelectedLabel("");
      return;
    }
    const option = options.find((opt) => opt.value === value);
    if (option) {
      setSelectedLabel(option.label);
    }
  }, [value, options]);
  const debouncedSearch = useMemo6(() => onSearch ? debounce(onSearch) : void 0, [onSearch]);
  const displayOptions = searchable && !onSearch ? options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase())) : options;
  const groupedOptions = useMemo6(() => groupOptionsByCategory(displayOptions), [displayOptions]);
  const handleChange = (newValue) => {
    setSearchValue("");
    onChange(newValue);
    onSearch?.("");
    if (newValue === null) {
      setSelectedLabel("");
    } else {
      const option = options.find((opt) => opt.value === newValue);
      if (option) setSelectedLabel(option.label);
    }
  };
  const handleSearch = (newSearch) => {
    setSearchValue(newSearch);
    debouncedSearch?.(newSearch);
  };
  const renderOption = (option) => {
    const { value: optionValue, category: _category, ...optionRest } = option;
    return /* @__PURE__ */ jsx37(
      SelectListOption,
      {
        value: optionValue,
        onClick: () => handleChange(optionValue ?? null),
        isSelected: optionValue === value,
        ...optionRest
      },
      String(optionValue)
    );
  };
  const hasError = error || !!errorMessage;
  return /* @__PURE__ */ jsxs23("div", { children: [
    /* @__PURE__ */ jsx37(FieldHeader, { label, required }),
    /* @__PURE__ */ jsx37(
      Dropdown,
      {
        open: isOpen,
        onOpenChange: setIsOpen,
        disabled,
        avoidCollisions,
        side,
        align,
        triggerComponent: triggerComponent ?? /* @__PURE__ */ jsx37(
          SelectFieldTrigger,
          {
            startIcon,
            "aria-label": "Select option",
            placeholder,
            disabled,
            valueLabel: selectedLabel,
            onClear: () => handleChange(null),
            isClearable: clearable,
            isLoading,
            error: hasError,
            variant
          }
        ),
        children: /* @__PURE__ */ jsxs23(SelectFieldContent, { children: [
          searchable && /* @__PURE__ */ jsx37(
            TextField,
            {
              ref: searchFieldRef,
              startIcon: IconSearch2,
              "aria-label": "Search options",
              placeholder: "Search\u2026",
              role: "searchbox",
              value: searchValue,
              onKeyDown: (e) => e.stopPropagation(),
              onChange: handleSearch,
              className: styles29.searchField
            }
          ),
          /* @__PURE__ */ jsxs23(SelectFieldContentList, { disabled: isLoading, children: [
            groupedOptions ? Object.entries(groupedOptions).map(([category, categoryOptions]) => /* @__PURE__ */ jsxs23(Fragment7, { children: [
              /* @__PURE__ */ jsx37(SelectFieldCategory, { label: category }),
              categoryOptions.map(renderOption)
            ] }, category)) : displayOptions.map(renderOption),
            noOptionsMessage && displayOptions.length === 0 && /* @__PURE__ */ jsx37(SelectListOption, { disabled: true, value: "empty", label: noOptionsMessage })
          ] })
        ] })
      }
    ),
    errorMessage && /* @__PURE__ */ jsx37(FieldFeedback, { message: errorMessage, variant: "error" })
  ] });
}

// src/components/editors/Switch/Switch.tsx
import clsx21 from "clsx";
import styles30 from "./Switch.module-O4OTIKPH.module.css";
import { jsx as jsx38, jsxs as jsxs24 } from "react/jsx-runtime";
var Switch = ({
  checked,
  onChange,
  disabled = false,
  className,
  label,
  ...props
}) => {
  const handleChange = (event) => {
    if (!disabled && onChange) {
      onChange(event.target.checked);
    }
  };
  if (!props["aria-label"]) {
    console.warn(
      "Switch component requires either a label prop or aria-label prop for accessibility"
    );
  }
  return /* @__PURE__ */ jsxs24(
    "label",
    {
      className: clsx21(
        className,
        styles30.switch,
        checked && styles30.checked,
        disabled && styles30.disabled
      ),
      children: [
        /* @__PURE__ */ jsx38(
          "input",
          {
            type: "checkbox",
            checked,
            onChange: handleChange,
            disabled,
            role: "switch",
            "aria-checked": checked,
            "aria-disabled": disabled,
            ...props
          }
        ),
        /* @__PURE__ */ jsx38("span", { className: styles30.switchTrack, "aria-hidden": "true", children: /* @__PURE__ */ jsx38("span", { className: styles30.switchThumb, "aria-hidden": "true" }) }),
        label && /* @__PURE__ */ jsx38("span", { className: clsx21(styles30.switchLabel), children: label })
      ]
    }
  );
};

// src/components/shared/ButtonIcon/ButtonIcon.tsx
import clsx22 from "clsx";
import styles31 from "./ButtonIcon.module-Y74WWGDY.module.css";
import { jsx as jsx39 } from "react/jsx-runtime";
var ButtonIcon = ({
  icon: Icon,
  size = "medium",
  variant = "primary",
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx39(
    "button",
    {
      className: clsx22(styles31.buttonIcon, styles31[variant], styles31[size], className),
      ...props,
      children: /* @__PURE__ */ jsx39(Icon, {})
    }
  );
};

// src/components/shared/Card/Card.tsx
import React4 from "react";
import styles32 from "./Card.module-YBJG5NGD.module.css";
import clsx23 from "clsx";
import { IconInfoCircle } from "@tabler/icons-react";

// src/components/shared/Tooltip/Tooltip.tsx
import * as RadixTooltip from "@radix-ui/react-tooltip";

// src/components/shared/Tooltip/Tooltip.utils.tsx
var EMBEDDABLE_RADIX_OVERLAY_ATTR = "embeddable-radix-overlays";
var getOrCreateTooltipOverlayContainer = () => {
  if (typeof document === "undefined") return null;
  let el = document.querySelector(`[${EMBEDDABLE_RADIX_OVERLAY_ATTR}]`);
  if (el) return el;
  el = document.createElement("div");
  el.setAttribute(EMBEDDABLE_RADIX_OVERLAY_ATTR, "true");
  el.style.position = "fixed";
  el.style.inset = "0";
  el.style.pointerEvents = "none";
  el.style.zIndex = "1000";
  document.body.appendChild(el);
  return el;
};
var tooltipContentStyle = {
  zIndex: 5,
  padding: "var(--em-tooltip-padding-top-bottom, 0.375rem) var(--em-tooltip-padding-left-right, 0.5rem)",
  borderRadius: "var(--em-tooltip-border-radius, 0.375rem)",
  background: "var(--em-tooltip-background, #212129)",
  color: "var(--em-tooltip-color, #fff)",
  fontFamily: "var(--em-tooltip-font-family, sans-serif)",
  fontSize: "var(--em-tooltip-font-size, 0.75rem)",
  fontWeight: "var(--em-tooltip-font-weight, 700)",
  lineHeight: "var(--em-tooltip-line-height, 0.875rem)",
  maxWidth: "var(--em-tooltip-max-width, 16rem)"
};

// src/components/shared/Tooltip/Tooltip.tsx
import { jsx as jsx40, jsxs as jsxs25 } from "react/jsx-runtime";
var Tooltip7 = ({
  side,
  align,
  trigger,
  children,
  delayDuration = 0
}) => {
  return /* @__PURE__ */ jsx40(RadixTooltip.Provider, { children: /* @__PURE__ */ jsxs25(RadixTooltip.Root, { delayDuration, children: [
    /* @__PURE__ */ jsx40(RadixTooltip.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx40(RadixTooltip.Portal, { container: getOrCreateTooltipOverlayContainer() ?? void 0, children: /* @__PURE__ */ jsx40(
      RadixTooltip.Content,
      {
        side,
        align,
        style: tooltipContentStyle,
        sideOffset: 4,
        children
      }
    ) })
  ] }) });
};

// src/components/shared/Card/Card.tsx
import { jsx as jsx41, jsxs as jsxs26 } from "react/jsx-runtime";
var Card = React4.forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx41("div", { className: clsx23(styles32.card, className), ref, ...props, children });
  }
);
Card.displayName = "Card";
var LONG_TOOLTIP_TEXT_LENGTH = 15;
var getTooltipText = (tooltip) => {
  if (typeof tooltip === "string" || typeof tooltip === "number") {
    return String(tooltip);
  }
  if (Array.isArray(tooltip)) {
    return tooltip.map(getTooltipText).join("");
  }
  if (React4.isValidElement(tooltip)) {
    return getTooltipText(tooltip.props.children);
  }
  return "";
};
var getCardHeaderTooltipAlign = (tooltip) => {
  const tooltipText = getTooltipText(tooltip).trim();
  return tooltipText.length > LONG_TOOLTIP_TEXT_LENGTH ? "end" : "center";
};
var CardHeader = ({ title, subtitle, tooltip }) => {
  if (!title && !subtitle && !tooltip) {
    return null;
  }
  return /* @__PURE__ */ jsxs26("div", { className: styles32.cardHeader, children: [
    /* @__PURE__ */ jsxs26("div", { className: styles32.cardHeaderTitle, children: [
      title && /* @__PURE__ */ jsx41("h1", { children: title }),
      tooltip && /* @__PURE__ */ jsx41(
        Tooltip7,
        {
          side: "top",
          align: getCardHeaderTooltipAlign(tooltip),
          trigger: /* @__PURE__ */ jsx41("button", { className: styles32.cardHeaderTooltipButton, "aria-label": "Info", children: /* @__PURE__ */ jsx41(IconInfoCircle, {}) }),
          children: tooltip
        }
      )
    ] }),
    subtitle && /* @__PURE__ */ jsx41("p", { children: subtitle })
  ] });
};
var CardContent = React4.forwardRef(
  ({ children }, ref) => {
    return /* @__PURE__ */ jsx41("div", { className: styles32.cardContent, ref, children });
  }
);
CardContent.displayName = "CardContent";

// src/components/shared/Card/CardFeedback/CardFeedback.tsx
import clsx24 from "clsx";
import styles33 from "./CardFeedback.module-WCSHLBSM.module.css";
import { jsx as jsx42, jsxs as jsxs27 } from "react/jsx-runtime";
var CardFeedback = ({
  icon: Icon,
  title,
  message,
  className,
  variant = "info"
}) => {
  return /* @__PURE__ */ jsxs27("div", { className: clsx24(styles33.feedback, styles33[variant], className), children: [
    Icon && /* @__PURE__ */ jsx42(Icon, {}),
    /* @__PURE__ */ jsx42("h2", { children: title }),
    /* @__PURE__ */ jsx42("p", { children: message })
  ] });
};

// src/components/shared/GhostButtonIcon/GhostButtonIcon.tsx
import React5 from "react";
import clsx25 from "clsx";
import style from "./GhostButtonIcon.module-6J6DJCE6.module.css";
import { jsx as jsx43 } from "react/jsx-runtime";
var GhostButtonIcon = React5.forwardRef(
  ({ icon: Icon, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx43("button", { ref, className: clsx25(style.ghostButtonIcon, className), ...props, children: /* @__PURE__ */ jsx43(Icon, {}) });
  }
);
GhostButtonIcon.displayName = "GhostButtonIcon";

// src/components/shared/Overlay/Overlay.tsx
import clsx26 from "clsx";
import styles34 from "./Overlay.module-FCW64VWM.module.css";
import { jsx as jsx44 } from "react/jsx-runtime";
var Overlay = ({ className, children }) => {
  return /* @__PURE__ */ jsx44("div", { className: clsx26(styles34.overlay, className), children });
};

// src/components/shared/Skeleton/Skeleton.tsx
import styles35 from "./Skeleton.module-ZN5S5VRF.module.css";
import { jsx as jsx45 } from "react/jsx-runtime";
var Skeleton = () => {
  return /* @__PURE__ */ jsx45("div", { className: styles35.skeleton });
};

// src/components/shared/Typography/Typography.tsx
import styles36 from "./Typography.module-SW6CT55P.module.css";
import clsx27 from "clsx";
import { jsx as jsx46 } from "react/jsx-runtime";
function Typography({
  children,
  as: Component = "p",
  className,
  style: style2,
  title
}) {
  return /* @__PURE__ */ jsx46(Component, { title, className: clsx27(styles36.typography, className), style: style2, children });
}

// src/components/shared/Divider/Divider.tsx
import clsx28 from "clsx";
import styles37 from "./Divider.module-2BOWHKT3.module.css";
import { jsx as jsx47 } from "react/jsx-runtime";
var getStyle2 = (color, thickness, vertical) => {
  const style2 = {};
  if (color) style2.backgroundColor = color;
  if (thickness) {
    if (vertical) {
      style2.width = thickness;
    } else {
      style2.height = thickness;
    }
  }
  return style2;
};
var Divider = ({ color, thickness, vertical, className }) => {
  return /* @__PURE__ */ jsx47(
    "div",
    {
      className: clsx28(styles37.divider, vertical && styles37.vertical, className),
      style: getStyle2(color, thickness, vertical)
    }
  );
};

// src/components/shared/Markdown/Markdown.tsx
import { useMemo as useMemo7 } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles38 from "./Markdown.module-AIJ2C4S2.module.css";
import { jsx as jsx48 } from "react/jsx-runtime";
var normalizeEscapedNewlines = (input) => input?.replaceAll(String.raw`\n`, "\n");
var Markdown = ({ content }) => {
  const resolvedContent = useMemo7(() => {
    return normalizeEscapedNewlines(content);
  }, [content]);
  return /* @__PURE__ */ jsx48("div", { className: styles38.markdown, children: /* @__PURE__ */ jsx48(ReactMarkdown, { remarkPlugins: [remarkGfm], children: resolvedContent }) });
};

// src/components/shared/MarkdownEditor/MarkdownEditor.tsx
import {
  IconBold,
  IconCode,
  IconEye,
  IconH1,
  IconItalic,
  IconLink,
  IconList,
  IconPencil,
  IconStrikethrough
} from "@tabler/icons-react";
import { useRef as useRef15, useState as useState11 } from "react";
import styles39 from "./MarkdownEditor.module-2R7P4ZIL.module.css";

// src/components/shared/MarkdownEditor/MarkdownEditor.utils.ts
var MAX_HISTORY = 100;
var applyWrap = (value, start, end, syntax) => value.slice(0, start) + syntax + value.slice(start, end) + syntax + value.slice(end);
var applyLinePrefix = (value, start, prefix) => {
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  return value.slice(0, lineStart) + prefix + value.slice(lineStart);
};

// src/components/shared/MarkdownEditor/MarkdownEditor.tsx
import { jsx as jsx49, jsxs as jsxs28 } from "react/jsx-runtime";
var MarkdownEditor = ({
  value: valueProp,
  onChange,
  placeholder
}) => {
  const value = valueProp ?? "";
  const textareaRef = useRef15(null);
  const [history, setHistory] = useState11({ entries: [value], index: 0 });
  const [isPreview, setIsPreview] = useState11(false);
  const handleChange = (newValue) => {
    setHistory((prev) => {
      const entries = [...prev.entries.slice(0, prev.index + 1), newValue];
      const capped = entries.length > MAX_HISTORY ? entries.slice(-MAX_HISTORY) : entries;
      return { entries: capped, index: capped.length - 1 };
    });
    onChange?.(newValue);
  };
  const applyEdit = (edit) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const { selectionStart: start, selectionEnd: end } = textarea;
    const result = edit(value, start, end);
    handleChange(result.value);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(...result.cursor);
    });
  };
  const handleWrap = (syntax) => applyEdit((text, start, end) => ({
    value: applyWrap(text, start, end, syntax),
    cursor: [start + syntax.length, end + syntax.length]
  }));
  const handlePrefix = (prefix) => applyEdit((text, start) => ({
    value: applyLinePrefix(text, start, prefix),
    cursor: [start + prefix.length, start + prefix.length]
  }));
  const handleLink = () => applyEdit((text, start, end) => ({
    value: text.slice(0, start) + `[${text.slice(start, end)}]()` + text.slice(end),
    cursor: [end + 3, end + 3]
    // cursor inside the ()
  }));
  const navigate = (delta) => {
    const newIndex = history.index + delta;
    if (newIndex < 0 || newIndex >= history.entries.length) return;
    const entry = history.entries[newIndex];
    if (entry === void 0) return;
    setHistory((prev) => ({ ...prev, index: newIndex }));
    onChange?.(entry);
  };
  const handleKeyDown = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    const key = `${e.shiftKey ? "shift+" : ""}${e.key.toLowerCase()}`;
    const actions = {
      z: () => navigate(-1),
      "shift+z": () => navigate(1),
      y: () => navigate(1),
      b: () => handleWrap("**"),
      i: () => handleWrap("*"),
      k: handleLink,
      "`": () => handleWrap("`"),
      "shift+x": () => handleWrap("~~")
    };
    const action = actions[key];
    if (action) {
      e.preventDefault();
      action();
    }
  };
  const toolbarButtons = [
    { icon: IconBold, label: "Bold", onClick: () => handleWrap("**") },
    { icon: IconItalic, label: "Italic", onClick: () => handleWrap("*") },
    { icon: IconStrikethrough, label: "Strikethrough", onClick: () => handleWrap("~~") },
    { icon: IconCode, label: "Code", onClick: () => handleWrap("`") },
    { icon: IconH1, label: "Heading", onClick: () => handlePrefix("# ") },
    { icon: IconList, label: "Bullet list", onClick: () => handlePrefix("- ") },
    { icon: IconLink, label: "Link", onClick: handleLink }
  ];
  return /* @__PURE__ */ jsxs28("div", { className: styles39.container, children: [
    /* @__PURE__ */ jsxs28("div", { className: styles39.toolbar, children: [
      toolbarButtons.map(({ icon, label, onClick }) => /* @__PURE__ */ jsx49(
        GhostButtonIcon,
        {
          icon,
          "aria-label": label,
          disabled: isPreview,
          onMouseDown: (e) => e.preventDefault(),
          onClick
        },
        label
      )),
      /* @__PURE__ */ jsx49("div", { className: styles39.toolbarSpacer }),
      /* @__PURE__ */ jsx49(
        GhostButtonIcon,
        {
          icon: isPreview ? IconPencil : IconEye,
          "aria-label": isPreview ? "Edit" : "Preview",
          onMouseDown: (e) => e.preventDefault(),
          onClick: () => setIsPreview((prev) => !prev)
        }
      )
    ] }),
    isPreview ? /* @__PURE__ */ jsx49("div", { className: styles39.preview, children: /* @__PURE__ */ jsx49(Markdown, { content: value }) }) : /* @__PURE__ */ jsx49(
      "textarea",
      {
        ref: textareaRef,
        className: styles39.markdownEditor,
        value,
        onChange: (e) => handleChange(e.target.value),
        onKeyDown: handleKeyDown,
        placeholder
      }
    )
  ] });
};

// src/utils/object.utils.ts
var shallowEqual = (object1, object2) => {
  const keys1 = Object.keys(object1 ?? {});
  const keys2 = Object.keys(object2 ?? {});
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (object1[key]?.toString() !== object2[key]?.toString()) {
      return false;
    }
  }
  return true;
};
export {
  ActionIcon,
  BarChart,
  BubbleChart,
  Button,
  ButtonIcon,
  Card,
  CardContent,
  CardFeedback,
  CardHeader,
  ChartTabs,
  DateRangePicker,
  DateRangePickerField,
  Divider,
  DonutChart,
  Dropdown,
  FieldFeedback,
  FieldHeader,
  GhostButton,
  GhostButtonIcon,
  HeatMap,
  KpiChart,
  KpiTrend,
  LineChart,
  Markdown,
  MarkdownEditor,
  MultiSelectField,
  NULL_BAND_OFFSET,
  NULL_BAND_PADDING,
  NumberField,
  Overlay,
  PieChart,
  PivotTable,
  ScatterChart,
  SelectFieldCategory,
  SelectFieldContent,
  SelectFieldContentList,
  SelectFieldTrigger,
  SelectListOption,
  SingleSelectField,
  Skeleton,
  Switch,
  TableBody,
  TableBodyCellWithCopy,
  TableHeader,
  TableHeaderAlign,
  TablePaginated,
  TablePagination,
  TableScrollable,
  TableSortDirection,
  TextField,
  Tooltip7 as Tooltip,
  Typography,
  chartjsAxisOptionsLayoutPadding,
  chartjsAxisOptionsLayoutPaddingLarge,
  computeScatterNullBand,
  createScatterNullBandPlugin,
  endOfDayUTC,
  filterNumericScatterData,
  getBarChartData,
  getBarChartOptions,
  getBubbleChartData,
  getBubbleChartOptions,
  getChartColors,
  getChartPointClicked,
  getChartjsAxisOptions,
  getChartjsAxisOptionsPlugins,
  getChartjsAxisOptionsScales,
  getChartjsAxisOptionsScalesGrid,
  getChartjsAxisOptionsScalesGridColor,
  getChartjsAxisOptionsScalesTicksDefault,
  getChartjsAxisOptionsScalesTicksMuted,
  getChartjsAxisOptionsScalesTitle,
  getColorWithOpacity,
  getDonutChartOptions,
  getKpiDisplayValue,
  getLineChartData,
  getLineChartOptions,
  getPieChartData,
  getPieChartOptions,
  getScatterChartData,
  getScatterChartOptions,
  getScatterChartPlugins,
  getScatterDataWithNullBand,
  getScatterNullBand,
  getStyle,
  getStyleNumber,
  getTableTotalPages,
  hasNullMeasure,
  isMeasureMissing,
  isSameDate,
  isSameDateRange,
  shallowEqual,
  styles,
  useDebounce,
  useResizeObserver,
  useTableGetRowsPerPage
};
//# sourceMappingURL=index.js.map